import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import RoadmapFinanceMockService from 'services/RoadmapFinanceMockService';
import PassiveInvoicesService from 'services/PassiveInvoicesService';
import { createRoadmapWorkspaceSeed, persistRoadmapWorkspace } from 'mocks/roadmapFinanceMock';
import {
    buildFinanceQuickSearchEntries,
    getDocumentStatusLabel,
    resolveCompanyDocumentId,
    resolveRoadmapBusinessRole,
} from 'utils/roadmapFinance';
import { recalculatePassiveWorkspace } from 'utils/roadmapFinancePassive';
import { recalculateTreasuryWorkspace } from 'utils/roadmapFinanceTreasury';
import { recalculateInventoryWorkspace } from 'utils/roadmapFinanceInventory';
import { recalculateActiveWorkspace } from 'utils/roadmapFinanceActive';
import { recalculateComplianceWorkspace } from 'utils/roadmapFinanceCompliance';
import { recalculateBillingWorkspace, formatRegistroNumber, getFreeInvoiceLinkedTypeLabel } from 'utils/roadmapFinanceBilling';
import { recalculateTemplateEditorWorkspace, TEMPLATE_FIELD_PRESETS } from 'utils/roadmapFinanceTemplateEditor';
import { buildAccountantPackage } from 'utils/roadmapFinanceAccountant';

const initialState = {
    loadingWorkspace: false,
    loadedWorkspace: false,
    errorWorkspace: false,
    activeCompanyDocumentId: null,
    businessRole: 'commerciale',
    companies: [],
    documents: [],
    archiveDocuments: [],
    selectedFiscalYears: [dayjs().year()],
    closedFiscalYears: Array.from({ length: 10 }, (_, i) => dayjs().year() - 10 + i),
    passiveWorkspace: {
        selectedInvoiceId: null,
        invoices: {},
        ddts: [],
        returnDdts: [],
        supplierDirectory: [],
        merchandiseGroups: [],
        articleCatalog: [],
        salesEntries: [],
    },
    treasuryWorkspace: {
        activeSourceId: null,
        activePayableId: null,
        activeMovementId: null,
        selectedLedgerSupplier: null,
        sources: [],
        payables: [],
        supplierCredits: [],
        bankMovements: [],
        imports: [],
        suggestions: [],
        supplierLedgers: [],
        summary: null,
    },
    inventoryWorkspace: {
        activeDepositId: null,
        selectedSerialId: null,
        articles: [],
        deposits: [],
        serials: [],
        transfers: [],
        consumptions: [],
        reorderProposals: [],
        articleStock: [],
        depositSummaries: [],
        pendingTrials: [],
        lowStockAlerts: [],
        obsoleteArticles: [],
        summary: null,
    },
    foreignInvoiceWorkspace: {
        activeForeignInvoiceId: null,
        foreignInvoices: [],
        customsDeclarations: [],
    },
    activeWorkspace: {
        activeAgreementId: null,
        activeQueueItemId: null,
        activeCreditNoteId: null,
        agreements: [],
        operativeQueue: [],
        creditNotes: [],
        reInvoices: [],
        receipts: [],
        customerLedgers: [],
        summary: null,
    },
    complianceWorkspace: {
        activeNsoOrderId: null,
        activePraticaId: null,
        activeFatturaPAId: null,
        activeStsExportId: null,
        nsoOrders: [],
        pratiche: [],
        fatturePA: [],
        stsExports: [],
        archiveEntries: [],
        accountantExports: [],
        summary: null,
    },
    billingWorkspace: {
        activeBillingChannel: 'b2c',
        activeB2bInvoiceId: null,
        activeFreeInvoiceId: null,
        activeDdtId: null,
        b2bInvoices: [],
        freeInvoices: [],
        activeDdts: [],
        registriSezionali: [],
        summary: null,
    },
    migrationWorkspace: {
        activeMigrationId: null,
        migrations: [],
    },
    templateEditorWorkspace: {
        activeTemplateId: null,
        templates: [],
        summary: null,
    },
    accountantPackageWorkspace: {
        activePackageId: null,
        packages: [],
    },
    notifications: [],
    playbooks: [],
    helpArticles: [],
    pendingSaves: [],
    billingHubPassive: {
        loading: false,
        loaded: false,
        error: null,
        lastSyncedAt: null,
        companyDocumentId: null,
    },
    networkMode: 'online',
    lastSyncedAt: null,
    loadedAt: null,
};

const persistState = (state) => {
    persistRoadmapWorkspace(state);
};

const addNotification = (state, notification) => {
    state.notifications = [notification, ...state.notifications].slice(0, 20);
};

const clonePassiveWorkspace = (workspace) => JSON.parse(JSON.stringify(workspace || {}));

const parseBillingPayload = (value) => {
    if (typeof value !== 'string') {
        return value;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

const getBillingHubPayload = (invoice) => {
    const rawPayload = invoice?.rawPayload || invoice?.raw_payload || invoice?.payload || null;
    const parsedRawPayload = parseBillingPayload(rawPayload) || rawPayload;

    if (parsedRawPayload?.fattura_elettronica_header || parsedRawPayload?.fattura_elettronica_body) {
        return parsedRawPayload;
    }

    const nestedPayload = parseBillingPayload(parsedRawPayload?.payload) || parsedRawPayload?.payload;

    if (nestedPayload?.fattura_elettronica_header || nestedPayload?.fattura_elettronica_body) {
        return nestedPayload;
    }

    const invoicePayload = parseBillingPayload(parsedRawPayload?.invoice?.payload) || parsedRawPayload?.invoice?.payload;

    if (invoicePayload) {
        return invoicePayload;
    }

    const dataPayload = parseBillingPayload(parsedRawPayload?.data) || parsedRawPayload?.data;

    if (dataPayload?.fattura_elettronica_header || dataPayload?.fattura_elettronica_body) {
        return dataPayload;
    }

    return parsedRawPayload || {};
};

const toBillingNumber = (value, fallback = 0) => {
    const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : fallback;
};

const getBillingDocument = (payload = {}) =>
    (Array.isArray(payload?.fattura_elettronica_body)
        ? payload.fattura_elettronica_body[0]
        : payload?.fattura_elettronica_body)?.dati_generali?.dati_generali_documento || {};

const getBillingSupplier = (payload = {}) =>
    payload?.fattura_elettronica_header?.cedente_prestatore?.dati_anagrafici || {};

const asBillingArray = (value) => (Array.isArray(value) ? value : [value].filter(Boolean));

const getBillingSummaryRows = (payload = {}) =>
    asBillingArray((Array.isArray(payload?.fattura_elettronica_body)
        ? payload.fattura_elettronica_body[0]
        : payload?.fattura_elettronica_body)?.dati_beni_servizi?.dati_riepilogo);

const getBillingLineRows = (payload = {}) =>
    asBillingArray((Array.isArray(payload?.fattura_elettronica_body)
        ? payload.fattura_elettronica_body[0]
        : payload?.fattura_elettronica_body)?.dati_beni_servizi?.dettaglio_linee);

const getBillingSupplierName = (supplier = {}, invoice = {}) =>
    supplier?.anagrafica?.denominazione ||
    [supplier?.anagrafica?.nome, supplier?.anagrafica?.cognome].filter(Boolean).join(' ') ||
    invoice?.supplierName ||
    invoice?.counterpartyName ||
    'Fornitore';

const mapBillingStatusToDocumentStatus = (status) => {
    const normalized = String(status || '').toUpperCase();
    if (normalized === 'PROCESSED') return 'contabilizzata';
    if (normalized === 'FAILED') return 'errore';
    if (normalized === 'AVAILABLE') return 'da_agganciare';
    if (normalized === 'PROCESSING' || normalized === 'QUEUED') return 'ricevuta';
    return 'ricevuta';
};

const isBillingInvoiceAvailable = (status) => {
    const normalized = String(status || '').trim().toUpperCase();
    return normalized === 'AVAILABLE' || normalized === 'AVAIBLE';
};

const isPassiveInvoiceDocument = (document) =>
    document?.type === 'supplier_invoice' || document?.type === 'service_invoice' || document?.type === 'supplier_credit_note';

export const buildBillingHubInvoiceModels = ({
    list = [],
    details = [],
    companyDocumentId,
    includeInvoiceIds = [],
}) => {
    const explicitlyIncluded = new Set(includeInvoiceIds.filter(Boolean));
    const detailsById = details.reduce((accumulator, detail) => {
        if (detail?.id) {
            accumulator[detail.id] = detail;
        }
        return accumulator;
    }, {});

    const documents = [];
    const invoices = {};

    list.forEach((item) => {
        const detail = detailsById[item.id] || item;
        const payload = getBillingHubPayload(detail);
        const document = getBillingDocument(payload);
        const supplier = getBillingSupplier(payload);
        const summaryRows = getBillingSummaryRows(payload);
        const lineRows = getBillingLineRows(payload);
        const documentType = String(document?.tipo_documento || item.documentType || detail.documentType || 'TD01').toUpperCase();
        const isCreditNote = documentType === 'TD04';
        const net = summaryRows.reduce((total, row) => total + toBillingNumber(row?.imponibile_importo, 0), 0);
        const vat = summaryRows.reduce((total, row) => total + toBillingNumber(row?.imposta, 0), 0);
        const total = net + vat;
        const supplierName = getBillingSupplierName(supplier, detail);
        const supplierVat = supplier?.id_fiscale_iva?.id_codice || detail?.supplierVat || '';
        const referenceNumber = document?.numero || detail?.filename || item.filename || item.id;
        const issueDate = document?.data || item.receivedAt || item.createdAt || dayjs().format('YYYY-MM-DD');
        const invoiceId = item.id;
        const status = item.status || detail.status;
        const normalizedStatus = String(status || '').toUpperCase();

        if (!isBillingInvoiceAvailable(status) && !explicitlyIncluded.has(item.id)) {
            return;
        }

        documents.push({
            id: invoiceId,
            title: `${isCreditNote ? 'Nota di credito ricevuta' : 'Fattura fornitore'} ${referenceNumber}`,
            type: isCreditNote ? 'supplier_credit_note' : lineRows.length > 0 ? 'supplier_invoice' : 'service_invoice',
            documentType,
            status: mapBillingStatusToDocumentStatus(status),
            companyDocumentId,
            counterpartyName: supplierName,
            amount: Number((isCreditNote ? Math.abs(total) : total).toFixed(2)),
            dueDate: issueDate,
            domain: 'finance',
            route: `/finance/passivo?invoice=${invoiceId}`,
            referenceNumber,
            tags: ['billing-hub', String(status || 'received').toLowerCase()],
            fiscalYear: issueDate ? dayjs(issueDate).year() : dayjs().year(),
            updatedAt: item.updatedAt || detail.updatedAt || dayjs().toISOString(),
            notes: [],
            billingHubInvoiceId: invoiceId,
            billingHubStatus: status,
        });

        invoices[invoiceId] = {
            id: invoiceId,
            documentType,
            companyDocumentId,
            origin: 'billing-hub',
            billingHubInvoiceId: invoiceId,
            billingHubStatus: status,
            acubeEvent: {
                invoice: {
                    uuid: detail.acubeInvoiceUuid || item.acubeInvoiceUuid || invoiceId,
                    filename: detail.filename || item.filename,
                    created_at: detail.receivedAt || item.receivedAt || item.createdAt,
                    marking: status || 'received',
                    notice: detail.lastError || null,
                    payload,
                },
            },
            supplierKnown: false,
            supplierUnlinkedManually: false,
            selectedDdtIds: [],
            lineOverrides: {},
            extraLines: [],
            directLines: [],
            defaultCostCenter: 'Audiomedical',
            fallbackPurchaseAccount: 'Merci c/acquisti',
            protocolRegister: 1,
            protocolNumber: 'da assegnare',
            reviewStatus: normalizedStatus === 'PROCESSED' ? 'contabilizzata' : 'working',
            issueDate,
            supplierReferenceNumber: referenceNumber,
            xmlSupplierName: supplierName,
            xmlSupplierVat: supplierVat,
            xmlTotals: summaryRows.length > 0 ? {
                net: Number((isCreditNote ? Math.abs(net) : net).toFixed(2)),
                vat: Number((isCreditNote ? Math.abs(vat) : vat).toFixed(2)),
                // Il totale che la fattura dichiara, non la somma dei riepiloghi:
                // e' quello che il fornitore chiede e che il backend usa per il
                // debito. Se i due divergono il documento e' incoerente.
                declaredGross: document?.importo_totale_documento !== undefined
                    ? Number((isCreditNote
                        ? Math.abs(toBillingNumber(document.importo_totale_documento, 0))
                        : toBillingNumber(document.importo_totale_documento, 0)).toFixed(2))
                    : null,
            } : null,
        };
    });

    return { documents, invoices };
};

const applyPassiveWorkspaceMutation = (state, recipe) => {
    const nextWorkspace = clonePassiveWorkspace(state.passiveWorkspace);
    recipe(nextWorkspace);
    state.passiveWorkspace = recalculatePassiveWorkspace(nextWorkspace);
    state.treasuryWorkspace = recalculateTreasuryWorkspace({
        treasuryWorkspace: state.treasuryWorkspace,
        passiveWorkspace: state.passiveWorkspace,
        documents: state.documents,
    });
    persistState(state);
};

const cloneTreasuryWorkspace = (workspace) => JSON.parse(JSON.stringify(workspace || {}));

const applyTreasuryWorkspaceMutation = (state, recipe) => {
    const nextWorkspace = cloneTreasuryWorkspace(state.treasuryWorkspace);
    recipe(nextWorkspace);
    state.treasuryWorkspace = recalculateTreasuryWorkspace({
        treasuryWorkspace: nextWorkspace,
        passiveWorkspace: state.passiveWorkspace,
        documents: state.documents,
    });
    persistState(state);
};

const cloneInventoryWorkspace = (workspace) => JSON.parse(JSON.stringify(workspace || {}));

const hasPassiveWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        Object.keys(workspace.invoices || {}).length > 0 ||
        (workspace.ddts || []).length > 0 ||
        (workspace.articleCatalog || []).length > 0
    );
};

const hasTreasuryWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        (workspace.sources || []).length > 0 ||
        (workspace.payables || []).length > 0 ||
        (workspace.bankMovements || []).length > 0
    );
};

const hasInventoryWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        (workspace.deposits || []).length > 0 ||
        (workspace.serials || []).length > 0 ||
        (workspace.articles || []).length > 0
    );
};

const applyInventoryWorkspaceMutation = (state, recipe) => {
    const nextWorkspace = cloneInventoryWorkspace(state.inventoryWorkspace);
    recipe(nextWorkspace);
    state.inventoryWorkspace = recalculateInventoryWorkspace({
        inventoryWorkspace: nextWorkspace,
    });
    persistState(state);
};

const cloneForeignInvoiceWorkspace = (workspace) => JSON.parse(JSON.stringify(workspace || {}));

const hasForeignInvoiceWorkspaceData = (workspace) => {
    return Boolean(workspace) && (workspace.foreignInvoices || []).length > 0;
};

const applyForeignInvoiceWorkspaceMutation = (state, recipe) => {
    const nextWorkspace = cloneForeignInvoiceWorkspace(state.foreignInvoiceWorkspace);
    recipe(nextWorkspace);
    state.foreignInvoiceWorkspace = nextWorkspace;
    persistState(state);
};

const cloneActiveWorkspace = (workspace) => JSON.parse(JSON.stringify(workspace || {}));

const hasActiveWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        (workspace.agreements || []).length > 0 ||
        (workspace.operativeQueue || []).length > 0
    );
};

const applyActiveWorkspaceMutation = (state, recipe) => {
    const nextWorkspace = cloneActiveWorkspace(state.activeWorkspace);
    recipe(nextWorkspace);
    state.activeWorkspace = recalculateActiveWorkspace(nextWorkspace);
    persistState(state);
};

const cloneBillingWorkspace = (workspace) => JSON.parse(JSON.stringify(workspace || {}));

const hasBillingWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        (workspace.b2bInvoices || []).length > 0 ||
        (workspace.registriSezionali || []).length > 0
    );
};

const applyBillingWorkspaceMutation = (state, recipe) => {
    const nextWorkspace = cloneBillingWorkspace(state.billingWorkspace);
    recipe(nextWorkspace);
    state.billingWorkspace = recalculateBillingWorkspace(nextWorkspace);
    persistState(state);
};

const cloneMigrationWorkspace = (workspace) => JSON.parse(JSON.stringify(workspace || {}));

export const bootstrapRoadmapWorkspace = createAsyncThunk(
    'financeRoadmap/bootstrap',
    async ({ roleName, activeCompany, companies }, { rejectWithValue }) => {
        try {
            return await RoadmapFinanceMockService.loadWorkspace({
                roleName,
                activeCompany,
                companies,
            });
        } catch (error) {
            return rejectWithValue(error?.message || 'Errore caricamento workspace mock');
        }
    },
    {
        condition: ({ activeCompany }, { getState }) => {
            const state = getState().financeRoadmap;
            const requestedCompanyDocumentId = resolveCompanyDocumentId(activeCompany) || state.activeCompanyDocumentId || null;
            const sameCompany = !requestedCompanyDocumentId || requestedCompanyDocumentId === state.activeCompanyDocumentId;
            const workspaceHealthy = hasPassiveWorkspaceData(state.passiveWorkspace)
                && hasTreasuryWorkspaceData(state.treasuryWorkspace)
                && hasInventoryWorkspaceData(state.inventoryWorkspace);

            if (state.loadingWorkspace) {
                return false;
            }

            if (state.loadedWorkspace && sameCompany && workspaceHealthy) {
                return false;
            }

            return true;
        },
    }
);

export const hydrateRoadmapWorkspaceFallback = createAsyncThunk(
    'financeRoadmap/hydrateFallback',
    async ({ roleName, activeCompany, companies }, { rejectWithValue }) => {
        try {
            return createRoadmapWorkspaceSeed({
                roleName: roleName || 'contabile',
                activeCompany: activeCompany || null,
                companies: companies || [],
            });
        } catch (error) {
            return rejectWithValue(error?.message || 'Errore bootstrap fallback workspace mock');
        }
    }
);

export const syncPassiveInvoicesFromBillingHub = createAsyncThunk(
    'financeRoadmap/syncPassiveInvoicesFromBillingHub',
    async ({ companyDocumentId, legalEntityId }, { rejectWithValue }) => {
        try {
            if (!legalEntityId) {
                throw new Error('Legal Entity Billing Hub non configurata per l\'azienda selezionata');
            }
            const listResponse = await PassiveInvoicesService.list({ status: 'AVAILABLE', legalEntityId });
            const list = Array.isArray(listResponse?.data)
                ? listResponse.data
                : Array.isArray(listResponse)
                    ? listResponse
                    : [];

            const details = await Promise.all(
                list.map(async (invoice) => {
                    try {
                        return await PassiveInvoicesService.detail(invoice.id, legalEntityId);
                    } catch (error) {
                        return invoice;
                    }
                })
            );

            return buildBillingHubInvoiceModels({
                list,
                details,
                companyDocumentId,
            });
        } catch (error) {
            return rejectWithValue(error?.response?.data?.error?.message || error?.message || 'Errore sync Billing Hub');
        }
    },
    {
        condition: (_, { getState }) => {
            const state = getState().financeRoadmap;
            return !state.billingHubPassive?.loading;
        },
    }
);

export const transitionRoadmapDocument = createAsyncThunk(
    'financeRoadmap/transitionDocument',
    async ({ documentId, nextStatus, note }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const document = state.documents.find((item) => item.id === documentId);

        if (!document) {
            return rejectWithValue('Documento non trovato');
        }

        const operationId = `save-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: documentId,
            label: `Sync ${document.title}`,
        }));
        dispatch(applyDocumentTransition({
            documentId,
            nextStatus,
            note,
        }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1100 : 350);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const flushRoadmapPendingSaves = createAsyncThunk(
    'financeRoadmap/flushPendingSaves',
    async (_, { getState, dispatch }) => {
        const state = getState().financeRoadmap;

        if (state.networkMode !== 'online') {
            return [];
        }

        const queue = state.pendingSaves.filter((save) => save.status === 'queued');
        for (const save of queue) {
            dispatch(markMockSaveSaving({ id: save.id }));
            await RoadmapFinanceMockService.wait(300);
            dispatch(completeMockSave({ id: save.id }));
        }

        return queue.map((item) => item.id);
    }
);

export const persistPassiveInvoiceDraft = createAsyncThunk(
    'financeRoadmap/persistPassiveInvoiceDraft',
    async ({ invoiceId, label }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const invoice = state.passiveWorkspace?.invoices?.[invoiceId];

        if (!invoice) {
            return rejectWithValue('Fattura passiva non trovata');
        }

        const operationId = `passive-save-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: invoiceId,
            label: label || `Bozza ${invoiceId}`,
        }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1000 : 350);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const importMockTreasuryStatement = createAsyncThunk(
    'financeRoadmap/importMockTreasuryStatement',
    async ({ sourceId, fileName }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const source = state.treasuryWorkspace?.sources?.find((item) => item.id === sourceId);

        if (!source) {
            return rejectWithValue('Fonte finanziaria non trovata');
        }

        const operationId = `treasury-import-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: sourceId,
            label: `Import ${fileName || 'estratto conto'}`,
        }));
        dispatch(addImportedStatementLocally({ sourceId, fileName }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1100 : 450);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const registerTreasuryPayment = createAsyncThunk(
    'financeRoadmap/registerTreasuryPayment',
    async ({ payableId, sourceId, amount, date, valueDate, note, movementId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const payable = state.treasuryWorkspace?.payables?.find((item) => item.id === payableId);

        if (!payable) {
            return rejectWithValue('Scadenza da pagare non trovata');
        }

        const operationId = `treasury-payment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: payableId,
            label: `Pagamento ${payable.label}`,
        }));
        dispatch(registerTreasuryPaymentLocally({
            payableId,
            sourceId,
            amount,
            date,
            valueDate,
            note,
            movementId,
        }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1000 : 350);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const applyTreasurySuggestion = createAsyncThunk(
    'financeRoadmap/applyTreasurySuggestion',
    async ({ suggestionId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const suggestion = state.treasuryWorkspace?.suggestions?.find((item) => item.id === suggestionId);

        if (!suggestion) {
            return rejectWithValue('Suggerimento di riconciliazione non trovato');
        }

        await dispatch(registerTreasuryPayment({
            payableId: suggestion.payableId,
            sourceId: state.treasuryWorkspace.bankMovements.find((movement) => movement.id === suggestion.movementId)?.sourceId,
            amount: suggestion.amount,
            date: state.treasuryWorkspace.bankMovements.find((movement) => movement.id === suggestion.movementId)?.date,
            valueDate: state.treasuryWorkspace.bankMovements.find((movement) => movement.id === suggestion.movementId)?.bookingDate,
            note: `Riconciliazione automatica: ${suggestion.reason}`,
            movementId: suggestion.movementId,
        }));

        return { applied: true, suggestionId };
    }
);

export const compensateTreasuryCredit = createAsyncThunk(
    'financeRoadmap/compensateTreasuryCredit',
    async ({ payableId, creditId, amount }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const payable = state.treasuryWorkspace?.payables?.find((item) => item.id === payableId);
        const credit = state.treasuryWorkspace?.supplierCredits?.find((item) => item.id === creditId);

        if (!payable || !credit) {
            return rejectWithValue('Scadenza o nota di credito non trovata');
        }

        const operationId = `treasury-credit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: payableId,
            label: `Compensa ${credit.label}`,
        }));
        dispatch(applyTreasuryCreditCompensationLocally({ payableId, creditId, amount }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 850 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const importInventoryCatalogSeed = createAsyncThunk(
    'financeRoadmap/importInventoryCatalogSeed',
    async (_, { getState, dispatch }) => {
        const operationId = `inventory-import-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: 'inventory-catalog',
            label: 'Import anagrafiche articoli pulite',
        }));
        dispatch(importInventoryCatalogLocally());

        const networkMode = getState().financeRoadmap.networkMode;
        if (networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(networkMode === 'unstable' ? 1000 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const createInventoryTransfer = createAsyncThunk(
    'financeRoadmap/createInventoryTransfer',
    async ({ fromDepositId, toDepositId, serialIds, operator, note }, { getState, dispatch }) => {
        const operationId = `inventory-transfer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: serialIds.join(','),
            label: 'Trasferimento interno magazzino',
        }));
        dispatch(createInventoryTransferLocally({ fromDepositId, toDepositId, serialIds, operator, note }));

        const networkMode = getState().financeRoadmap.networkMode;
        if (networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(networkMode === 'unstable' ? 1000 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const startInventoryTrial = createAsyncThunk(
    'financeRoadmap/startInventoryTrial',
    async ({ serialId, patientName, depositId, operator, note }, { getState, dispatch }) => {
        const operationId = `inventory-trial-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: serialId,
            label: 'DDT contovisione mock',
        }));
        dispatch(startInventoryTrialLocally({ serialId, patientName, depositId, operator, note }));

        const networkMode = getState().financeRoadmap.networkMode;
        if (networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(networkMode === 'unstable' ? 1000 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const consumeInventoryMaterial = createAsyncThunk(
    'financeRoadmap/consumeInventoryMaterial',
    async ({ articleCode, depositId, quantity, operator, note }, { getState, dispatch }) => {
        const operationId = `inventory-consume-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: articleCode,
            label: 'Consumo laboratorio mock',
        }));
        dispatch(consumeInventoryMaterialLocally({ articleCode, depositId, quantity, operator, note }));

        const networkMode = getState().financeRoadmap.networkMode;
        if (networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(networkMode === 'unstable' ? 900 : 250);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const generateInventoryReorder = createAsyncThunk(
    'financeRoadmap/generateInventoryReorder',
    async ({ articleCode }, { getState, dispatch }) => {
        const operationId = `inventory-reorder-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: articleCode,
            label: 'Proposta ordine fornitore',
        }));
        dispatch(generateInventoryReorderLocally({ articleCode }));

        const networkMode = getState().financeRoadmap.networkMode;
        if (networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(networkMode === 'unstable' ? 800 : 250);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const saveForeignInvoiceDraft = createAsyncThunk(
    'financeRoadmap/saveForeignInvoiceDraft',
    async ({ invoiceId, label }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const invoice = (state.foreignInvoiceWorkspace?.foreignInvoices || []).find((inv) => inv.id === invoiceId);

        if (!invoice) {
            return rejectWithValue('Fattura estera non trovata');
        }

        const operationId = `foreign-save-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: invoiceId,
            label: label || `Bozza estera ${invoiceId}`,
        }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 350);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const finalizeForeignInvoice = createAsyncThunk(
    'financeRoadmap/finalizeForeignInvoice',
    async ({ invoiceId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const invoice = (state.foreignInvoiceWorkspace?.foreignInvoices || []).find((inv) => inv.id === invoiceId);

        if (!invoice) {
            return rejectWithValue('Fattura estera non trovata');
        }

        if (!invoice.supplierName || !invoice.referenceNumber) {
            return rejectWithValue('Fornitore e numero fattura obbligatori prima della contabilizzazione');
        }

        const operationId = `foreign-finalize-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: invoiceId,
            label: `Contabilizza estera ${invoiceId}`,
        }));
        dispatch(commitForeignInvoiceLocally({ invoiceId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1200 : 450);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const confirmReturnDdt = createAsyncThunk(
    'financeRoadmap/confirmReturnDdt',
    async ({ returnDdtId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const returnDdt = (state.passiveWorkspace?.returnDdts || []).find((r) => r.id === returnDdtId);

        if (!returnDdt) {
            return rejectWithValue('DDT di ritorno non trovato');
        }

        if (returnDdt.status !== 'bozza') {
            return rejectWithValue('Solo i DDT di ritorno in bozza possono essere inviati');
        }

        const operationId = `return-submit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: returnDdtId,
            label: `Invia reso ${returnDdt.internalNumber}`,
        }));
        dispatch(submitReturnDdtLocally({ returnDdtId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1000 : 400);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const registerPassiveCreditNote = createAsyncThunk(
    'financeRoadmap/registerPassiveCreditNote',
    async ({ returnDdtId, creditNoteReference, creditNoteDate, creditNoteTotal }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const returnDdt = (state.passiveWorkspace?.returnDdts || []).find((r) => r.id === returnDdtId);

        if (!returnDdt) {
            return rejectWithValue('DDT di ritorno non trovato');
        }

        if (returnDdt.linkedCreditNoteId) {
            return rejectWithValue('Nota di credito già registrata per questo reso');
        }

        if (!creditNoteReference || !creditNoteTotal) {
            return rejectWithValue('Numero documento e importo obbligatori');
        }

        const operationId = `credit-note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({
            id: operationId,
            entityId: returnDdtId,
            label: `NC ${creditNoteReference}`,
        }));
        dispatch(linkCreditNoteToReturnLocally({ returnDdtId, creditNoteReference, creditNoteDate, creditNoteTotal }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 350);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const confirmActiveAgreement = createAsyncThunk(
    'financeRoadmap/confirmActiveAgreement',
    async ({ agreementId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const agreement = (state.activeWorkspace?.agreements || []).find((a) => a.id === agreementId);

        if (!agreement) {
            return rejectWithValue('Accordo non trovato');
        }

        if (agreement.status !== 'bozza') {
            return rejectWithValue('Solo accordi in bozza possono essere confermati');
        }

        const operationId = `active-confirm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: agreementId, label: `Conferma accordo ${agreement.patientName}` }));
        dispatch(confirmActiveAgreementLocally({ agreementId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1000 : 350);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const sendActiveAgreementToAdmin = createAsyncThunk(
    'financeRoadmap/sendActiveAgreementToAdmin',
    async ({ agreementId, note }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const agreement = (state.activeWorkspace?.agreements || []).find((a) => a.id === agreementId);

        if (!agreement) {
            return rejectWithValue('Accordo non trovato');
        }

        if (!['confermato', 'bozza'].includes(agreement.status)) {
            return rejectWithValue('L\'accordo deve essere confermato prima di inviarlo in amministrazione');
        }

        const operationId = `active-send-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: agreementId, label: `Invia in amm. accordo ${agreement.patientName}` }));
        dispatch(sendActiveAgreementToAdminLocally({ agreementId, note }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1000 : 400);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const pushPatientAgreementToQueue = createAsyncThunk(
    'financeRoadmap/pushPatientAgreementToQueue',
    async ({ patientId, patientName, amount, note, priority, companyDocumentId, pianoScadenze }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;

        const operationId = `patient-queue-push-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: patientId, label: `Invia in fatturazione – ${patientName || 'Paziente'}` }));
        dispatch(pushPatientAgreementToQueueLocally({ patientId, patientName, amount, note, priority, companyDocumentId, pianoScadenze }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1000 : 400);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const markQueueItemComplete = createAsyncThunk(
    'financeRoadmap/markQueueItemComplete',
    async ({ queueItemId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const item = (state.activeWorkspace?.operativeQueue || []).find((q) => q.id === queueItemId);

        if (!item) {
            return rejectWithValue('Elemento coda non trovato');
        }

        const operationId = `active-queue-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: queueItemId, label: `Chiudi coda ${item.title}` }));
        dispatch(markQueueItemCompleteLocally({ queueItemId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const emitActiveCreditNote = createAsyncThunk(
    'financeRoadmap/emitActiveCreditNote',
    async ({ creditNoteId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const creditNote = (state.activeWorkspace?.creditNotes || []).find((cn) => cn.id === creditNoteId);

        if (!creditNote) {
            return rejectWithValue('Nota di credito non trovata');
        }

        if (creditNote.status !== 'bozza') {
            return rejectWithValue('Solo le note di credito in bozza possono essere emesse');
        }

        const operationId = `active-credit-emit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: creditNoteId, label: `Emetti NC ${creditNoteId}` }));
        dispatch(emitActiveCreditNoteLocally({ creditNoteId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1100 : 450);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const emitActiveReInvoice = createAsyncThunk(
    'financeRoadmap/emitActiveReInvoice',
    async ({ reInvoiceId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const reInvoice = (state.activeWorkspace?.reInvoices || []).find((ri) => ri.id === reInvoiceId);

        if (!reInvoice) {
            return rejectWithValue('Rifattura non trovata');
        }

        if (reInvoice.status !== 'bozza') {
            return rejectWithValue('Solo le rifatture in bozza possono essere emesse');
        }

        const operationId = `active-reinv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: reInvoiceId, label: `Emetti rifattura ${reInvoiceId}` }));
        dispatch(emitActiveReInvoiceLocally({ reInvoiceId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1100 : 450);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const emitActiveReceipt = createAsyncThunk(
    'financeRoadmap/emitActiveReceipt',
    async ({ receiptId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const receipt = (state.activeWorkspace?.receipts || []).find((r) => r.id === receiptId);

        if (!receipt) {
            return rejectWithValue('Corrispettivo non trovato');
        }

        if (receipt.status !== 'bozza') {
            return rejectWithValue('Solo i corrispettivi in bozza possono essere emessi');
        }

        const operationId = `active-receipt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: receiptId, label: `Emetti corrispettivo ${receiptId}` }));
        dispatch(emitActiveReceiptLocally({ receiptId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const cancelActiveReceipt = createAsyncThunk(
    'financeRoadmap/cancelActiveReceipt',
    async ({ receiptId, reason }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const receipt = (state.activeWorkspace?.receipts || []).find((r) => r.id === receiptId);

        if (!receipt) {
            return rejectWithValue('Corrispettivo non trovato');
        }

        if (receipt.status !== 'emesso') {
            return rejectWithValue('Solo i corrispettivi emessi possono essere annullati');
        }

        const operationId = `active-receipt-cancel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: receiptId, label: `Annulla corrispettivo ${receiptId}` }));
        dispatch(cancelActiveReceiptLocally({ receiptId, reason: reason || '' }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const emitB2bInvoice = createAsyncThunk(
    'financeRoadmap/emitB2bInvoice',
    async ({ invoiceId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const invoice = (state.billingWorkspace?.b2bInvoices || []).find((inv) => inv.id === invoiceId);

        if (!invoice) {
            return rejectWithValue('Fattura B2B non trovata');
        }

        if (invoice.status !== 'bozza') {
            return rejectWithValue('Solo le fatture B2B in bozza possono essere emesse');
        }

        const operationId = `billing-b2b-emit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: invoiceId, label: `Emissione fattura B2B ${invoice.referenceNumber || invoiceId}` }));
        dispatch(emitB2bInvoiceLocally({ invoiceId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1100 : 450);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const emitFreeInvoice = createAsyncThunk(
    'financeRoadmap/emitFreeInvoice',
    async ({ invoiceId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const invoice = (state.billingWorkspace?.freeInvoices || []).find((inv) => inv.id === invoiceId);

        if (!invoice) {
            return rejectWithValue('Fattura libera non trovata');
        }

        if (invoice.status !== 'bozza') {
            return rejectWithValue('Solo le fatture libere in bozza possono essere emesse');
        }

        const operationId = `billing-free-emit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: invoiceId, label: `Emissione fattura libera ${invoice.referenceNumber || invoiceId}` }));
        dispatch(emitFreeInvoiceLocally({ invoiceId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1100 : 450);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const reconcileFreeInvoice = createAsyncThunk(
    async ({ invoiceId, linkedType, linkedId, linkedLabel, note }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const invoice = (state.billingWorkspace?.freeInvoices || []).find((inv) => inv.id === invoiceId);

        if (!invoice) {
            return rejectWithValue('Fattura libera non trovata');
        }

        if (invoice.reconciliationStatus !== 'da_riconciliare') {
            return rejectWithValue('La fattura non è in attesa di riconciliazione');
        }

        if (!linkedType || !linkedId) {
            return rejectWithValue('Tipo e riferimento dell\'entità collegata sono obbligatori');
        }

        const operationId = `billing-free-reconcile-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: invoiceId, label: `Riconciliazione ${invoice.referenceNumber || invoiceId}` }));
        dispatch(reconcileFreeInvoiceLocally({ invoiceId, linkedType, linkedId, linkedLabel, note }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 350);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const emitActiveDdt = createAsyncThunk(
    'financeRoadmap/emitActiveDdt',
    async ({ ddtId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const ddt = (state.billingWorkspace?.activeDdts || []).find((d) => d.id === ddtId);

        if (!ddt) {
            return rejectWithValue('DDT di vendita non trovato');
        }

        if (ddt.status !== 'bozza') {
            return rejectWithValue('Solo i DDT in bozza possono essere emessi');
        }

        const operationId = `billing-ddt-emit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: ddtId, label: `Emissione DDT ${ddt.ddtNumber || ddtId}` }));
        dispatch(emitActiveDdtLocally({ ddtId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const createInvoiceFromDdts = createAsyncThunk(
    'financeRoadmap/createInvoiceFromDdts',
    async ({ ddtIds, channel, companyDocumentId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const ddts = (state.billingWorkspace?.activeDdts || []).filter((d) => ddtIds.includes(d.id));

        if (ddts.length === 0) {
            return rejectWithValue('Nessun DDT trovato con gli ID forniti');
        }

        const notEmitted = ddts.filter((d) => d.status !== 'emesso');
        if (notEmitted.length > 0) {
            return rejectWithValue('Tutti i DDT devono essere in stato emesso per procedere alla fatturazione');
        }

        const operationId = `billing-ddt-invoice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: ddtIds.join(','), label: `Fattura differita da ${ddts.length} DDT` }));
        dispatch(createInvoiceFromDdtsLocally({ ddtIds, channel: channel || ddts[0]?.channel || 'b2b', companyDocumentId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1100 : 400);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const emitImmediateInvoice = createAsyncThunk(
    'financeRoadmap/emitImmediateInvoice',
    async ({ customerName, customerFiscalCode, customerVatNumber, channel, companyDocumentId, lines, paymentTerms, createDdt }, { getState, dispatch }) => {
        const state = getState().financeRoadmap;
        const operationId = `billing-immediate-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: customerName || 'cliente', label: `Vendita immediata ${customerName || ''}` }));
        dispatch(emitImmediateInvoiceLocally({ customerName, customerFiscalCode, customerVatNumber, channel, companyDocumentId, lines, paymentTerms, createDdt }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1100 : 400);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const convertTrialToAgreement = createAsyncThunk(
    'financeRoadmap/convertTrialToAgreement',
    async ({ serialId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const serial = (state.inventoryWorkspace?.serials || []).find((s) => s.id === serialId);

        if (!serial) {
            return rejectWithValue('Matricola non trovata');
        }

        if (serial.status !== 'in_prova') {
            return rejectWithValue('La matricola non è in prova');
        }

        const alreadyLinked = (state.activeWorkspace?.agreements || []).some((a) => a.serialId === serialId && a.status !== 'annullato');
        if (alreadyLinked) {
            return rejectWithValue('Esiste già un accordo attivo per questa matricola');
        }

        const operationId = `active-trial-convert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: serialId, label: `Chiusura accordo da prova ${serial.serialNumber}` }));
        dispatch(convertTrialToAgreementLocally({ serialId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const receiveNsoOrder = createAsyncThunk(
    'financeRoadmap/receiveNsoOrder',
    async ({ nsoOrderId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const order = (state.complianceWorkspace?.nsoOrders || []).find((o) => o.id === nsoOrderId);
        if (!order) {
            return rejectWithValue('Ordine NSO non trovato');
        }

        const operationId = `compliance-nso-receive-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: nsoOrderId, label: `Presa in carico ordine ${order.nsoNumber}` }));
        dispatch(receiveNsoOrderLocally({ nsoOrderId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 700 : 250);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const evadeNsoOrder = createAsyncThunk(
    'financeRoadmap/evadeNsoOrder',
    async ({ nsoOrderId, linkedPraticaId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const order = (state.complianceWorkspace?.nsoOrders || []).find((o) => o.id === nsoOrderId);
        if (!order) {
            return rejectWithValue('Ordine NSO non trovato');
        }

        const operationId = `compliance-nso-evade-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: nsoOrderId, label: `Evasione ordine ${order.nsoNumber}` }));
        dispatch(evadeNsoOrderLocally({ nsoOrderId, linkedPraticaId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 700 : 250);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const importNsoOrderFromXml = createAsyncThunk(
    'financeRoadmap/importNsoOrderFromXml',
    async ({ parsedOrder, companyDocumentId }, { getState, dispatch }) => {
        const state = getState().financeRoadmap;
        const orderId = `nso-order-xml-${Date.now()}`;
        const order = {
            id: orderId,
            companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
            nsoNumber: parsedOrder.nsoNumber || `NSO-${Date.now()}`,
            aslName: parsedOrder.aslName || '',
            aslCodiceUnivoco: parsedOrder.aslCodiceUnivoco || '',
            patientName: parsedOrder.patientName || '',
            patientCf: parsedOrder.patientCf || '',
            articleCode: parsedOrder.articleCode || '',
            articleDescription: parsedOrder.articleDescription || '',
            requestedQuantity: parsedOrder.requestedQuantity || 1,
            requestedAmount: parsedOrder.requestedAmount || 0,
            status: 'ricevuto',
            receivedAt: dayjs().format('YYYY-MM-DD'),
            processedAt: null,
            linkedPraticaId: null,
            notes: parsedOrder.notes || `Importato da XML ${parsedOrder.orderType || 'NSO'} il ${dayjs().format('DD/MM/YYYY')}.`,
            importedFromXml: true,
            xmlOrderType: parsedOrder.orderType || 'NSO',
        };

        const operationId = `compliance-nso-import-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: orderId, label: `Import ordine ${order.nsoNumber}` }));
        dispatch(addNsoOrderLocally({ order }));

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { orderId, operationId };
    }
);

export const emitFatturaPA = createAsyncThunk(
    'financeRoadmap/emitFatturaPA',
    async ({ fatturaPAId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const fattura = (state.complianceWorkspace?.fatturePA || []).find((f) => f.id === fatturaPAId);
        if (!fattura) {
            return rejectWithValue('Fattura PA non trovata');
        }

        if (fattura.status !== 'bozza') {
            return rejectWithValue('La fattura PA non è in bozza');
        }

        const operationId = `compliance-fpa-emit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: fatturaPAId, label: `Invio SDI fattura ${fattura.referenceNumber}` }));
        dispatch(emitFatturaPALocally({ fatturaPAId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 1000 : 400);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const submitStsExport = createAsyncThunk(
    'financeRoadmap/submitStsExport',
    async ({ stsExportId }, { getState, dispatch, rejectWithValue }) => {
        const state = getState().financeRoadmap;
        const stsExport = (state.complianceWorkspace?.stsExports || []).find((s) => s.id === stsExportId);
        if (!stsExport) {
            return rejectWithValue('Export STS non trovato');
        }

        if (stsExport.status !== 'pronta_export') {
            return rejectWithValue("L'export STS non è in stato pronta_export");
        }

        const operationId = `compliance-sts-submit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: stsExportId, label: `Invio STS ${stsExport.period}` }));
        dispatch(submitStsExportLocally({ stsExportId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 350);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const generateAccountantExport = createAsyncThunk(
    'financeRoadmap/generateAccountantExport',
    async ({ type, period, companyDocumentId }, { getState, dispatch }) => {
        const state = getState().financeRoadmap;
        const operationId = `compliance-acc-export-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        dispatch(startMockSave({ id: operationId, entityId: type, label: `Generazione export ${type} ${period}` }));
        dispatch(addAccountantExportLocally({ type, period, companyDocumentId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 800 : 300);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const generateAccountantPackage = createAsyncThunk(
    'financeRoadmap/generateAccountantPackage',
    async ({ quarter, year, companyDocumentId }, { getState, dispatch }) => {
        const state = getState().financeRoadmap;
        const period = `${quarter} ${year}`;
        const operationId = `pkg-${quarter.toLowerCase()}-${year}-${Date.now()}`;
        dispatch(startMockSave({ id: operationId, entityId: period, label: `Generazione pacchetto trimestrale ${period}` }));
        dispatch(generateAccountantPackageLocally({ quarter, year, companyDocumentId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 900 : 400);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

export const markAccountantPackageSent = createAsyncThunk(
    'financeRoadmap/markAccountantPackageSent',
    async ({ packageId }, { getState, dispatch }) => {
        const state = getState().financeRoadmap;
        const operationId = `pkg-sent-${packageId}-${Date.now()}`;
        dispatch(startMockSave({ id: operationId, entityId: packageId, label: 'Invio pacchetto al commercialista' }));
        dispatch(markAccountantPackageSentLocally({ packageId }));

        if (state.networkMode === 'offline') {
            dispatch(queueMockSave({ id: operationId }));
            return { queued: true, operationId };
        }

        await RoadmapFinanceMockService.wait(state.networkMode === 'unstable' ? 600 : 200);
        dispatch(completeMockSave({ id: operationId }));
        return { queued: false, operationId };
    }
);

const financeRoadmapSlice = createSlice({
    name: 'financeRoadmap',
    initialState,
    reducers: {
        setNetworkMode: (state, action) => {
            state.networkMode = action.payload;
            persistState(state);
        },
        markNotificationRead: (state, action) => {
            state.notifications = state.notifications.map((notification) =>
                notification.id === action.payload ? { ...notification, unread: false } : notification
            );
            persistState(state);
        },
        markAllNotificationsRead: (state) => {
            state.notifications = state.notifications.map((notification) => ({
                ...notification,
                unread: false,
            }));
            persistState(state);
        },
        dismissNotification: (state, action) => {
            state.notifications = state.notifications.filter((notification) => notification.id !== action.payload);
            persistState(state);
        },
        setActiveRoadmapCompany: (state, action) => {
            state.activeCompanyDocumentId = action.payload;
            persistState(state);
        },
        setSelectedFiscalYears: (state, action) => {
            state.selectedFiscalYears = action.payload;
            persistState(state);
        },
        toggleFiscalYearClosed: (state, action) => {
            const year = action.payload;
            const closedFiscalYears = state.closedFiscalYears || [];
            const idx = closedFiscalYears.indexOf(year);
            if (idx >= 0) {
                state.closedFiscalYears = closedFiscalYears.filter((y) => y !== year);
            } else {
                state.closedFiscalYears = [...closedFiscalYears, year].sort((a, b) => b - a);
            }
            persistState(state);
        },
        setSelectedTreasurySource: (state, action) => {
            if (state.treasuryWorkspace) {
                state.treasuryWorkspace.activeSourceId = action.payload;
                persistState(state);
            }
        },
        setSelectedTreasuryPayable: (state, action) => {
            if (state.treasuryWorkspace) {
                state.treasuryWorkspace.activePayableId = action.payload;
                persistState(state);
            }
        },
        setSelectedTreasuryLedgerSupplier: (state, action) => {
            if (state.treasuryWorkspace) {
                state.treasuryWorkspace.selectedLedgerSupplier = action.payload;
                persistState(state);
            }
        },
        setSelectedInventoryDeposit: (state, action) => {
            if (state.inventoryWorkspace) {
                state.inventoryWorkspace.activeDepositId = action.payload;
                persistState(state);
            }
        },
        setSelectedInventorySerial: (state, action) => {
            if (state.inventoryWorkspace) {
                state.inventoryWorkspace.selectedSerialId = action.payload;
                persistState(state);
            }
        },
        repairRoadmapWorkspaceFromSeed: (state) => {
            const seed = createRoadmapWorkspaceSeed({
                roleName: state.businessRole || 'contabile',
                activeCompany: state.activeCompanyDocumentId ? { documentId: state.activeCompanyDocumentId } : null,
                companies: state.companies || [],
            });
            const documents = Array.isArray(state.documents) && state.documents.length > 0 ? state.documents : seed.documents;
            const passiveWorkspace = hasPassiveWorkspaceData(state.passiveWorkspace)
                ? recalculatePassiveWorkspace(state.passiveWorkspace)
                : seed.passiveWorkspace;
            const inventoryWorkspace = hasInventoryWorkspaceData(state.inventoryWorkspace)
                ? recalculateInventoryWorkspace({ inventoryWorkspace: state.inventoryWorkspace })
                : seed.inventoryWorkspace;
            const treasuryWorkspace = recalculateTreasuryWorkspace({
                treasuryWorkspace: hasTreasuryWorkspaceData(state.treasuryWorkspace)
                    ? state.treasuryWorkspace
                    : seed.treasuryWorkspace,
                passiveWorkspace,
                documents,
            });

            state.companies = seed.companies;
            state.documents = documents;
            state.archiveDocuments = seed.archiveDocuments;
            state.passiveWorkspace = passiveWorkspace;
            state.treasuryWorkspace = treasuryWorkspace;
            state.inventoryWorkspace = inventoryWorkspace;
            state.foreignInvoiceWorkspace = hasForeignInvoiceWorkspaceData(state.foreignInvoiceWorkspace)
                ? state.foreignInvoiceWorkspace
                : seed.foreignInvoiceWorkspace;
            state.activeWorkspace = hasActiveWorkspaceData(state.activeWorkspace)
                ? recalculateActiveWorkspace(state.activeWorkspace)
                : seed.activeWorkspace;
            state.billingWorkspace = hasBillingWorkspaceData(state.billingWorkspace)
                ? recalculateBillingWorkspace({
                    ...state.billingWorkspace,
                    registriSezionali: (state.billingWorkspace.registriSezionali || []).length > 0
                        ? state.billingWorkspace.registriSezionali
                        : seed.billingWorkspace.registriSezionali,
                })
                : seed.billingWorkspace;
            state.notifications = Array.isArray(state.notifications) && state.notifications.length > 0 ? state.notifications : seed.notifications;
            state.playbooks = Array.isArray(state.playbooks) && state.playbooks.length > 0 ? state.playbooks : seed.playbooks;
            state.helpArticles = Array.isArray(state.helpArticles) && state.helpArticles.length > 0 ? state.helpArticles : seed.helpArticles;
            state.activeCompanyDocumentId = state.activeCompanyDocumentId || seed.activeCompanyDocumentId;
            state.businessRole = state.businessRole || seed.businessRole;
            state.loadedWorkspace = true;
            state.errorWorkspace = false;

            addNotification(state, {
                id: `notif-roadmap-repair-${Date.now()}`,
                level: 'warning',
                unread: true,
                title: 'Workspace finance riallineato',
                description: 'I rami shared del mock frontend sono stati ricostruiti dal seed locale per evitare refresh manuali.',
                route: '/finance/shell',
                companyDocumentId: state.activeCompanyDocumentId || seed.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        setSelectedPassiveInvoice: (state, action) => {
            if (state.passiveWorkspace) {
                state.passiveWorkspace.selectedInvoiceId = action.payload;
                persistState(state);
            }
        },
        replacePassiveDdts: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                workspace.ddts = action.payload.ddts || [];
                if (action.payload.merchandiseGroups) {
                    workspace.merchandiseGroups = action.payload.merchandiseGroups;
                }
            });
        },
        setPassiveSelectedDdts: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId, selectedDdtIds } = action.payload;
                if (workspace.invoices?.[invoiceId]) {
                    workspace.invoices[invoiceId].selectedDdtIds = selectedDdtIds;
                }
            });
        },
        applyPassiveAutoMatch: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                invoice.selectedDdtIds = invoice.autoMatchCandidateDdtIds || [];
                invoice.reviewStatus = 'auto-matched';
            });
        },
        updatePassiveLine: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId, lineId, sourceType, changes } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                if (sourceType === 'extra') {
                    invoice.extraLines = (invoice.extraLines || []).map((line) =>
                        line.id === lineId ? { ...line, ...changes } : line
                    );
                    return;
                }

                if (sourceType === 'direct') {
                    invoice.directLines = (invoice.directLines || []).map((line) =>
                        line.id === lineId ? { ...line, ...changes } : line
                    );
                    return;
                }

                invoice.lineOverrides = invoice.lineOverrides || {};
                invoice.lineOverrides[lineId] = {
                    ...(invoice.lineOverrides[lineId] || {}),
                    ...changes,
                };
            });
        },
        updatePassiveJournalGroup: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId, lineIds, changes } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                invoice.lineOverrides = invoice.lineOverrides || {};
                (lineIds || []).forEach((lineId) => {
                    invoice.lineOverrides[lineId] = {
                        ...(invoice.lineOverrides[lineId] || {}),
                        ...changes,
                    };
                });
            });
        },
        addPassiveExtraLine: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                invoice.extraLines = [
                    ...(invoice.extraLines || []),
                    {
                        id: `extra-${invoiceId}-${Date.now()}`,
                        articleCode: 'SPESE-SPED',
                        description: 'Nuova riga servizio',
                        groupId: 'spedizione',
                        quantity: 1,
                        unitPrice: 0,
                        discount: 0,
                        vatRate: 22,
                        isService: true,
                    },
                ];
            });
        },
        removePassiveExtraLine: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId, lineId } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                invoice.extraLines = (invoice.extraLines || []).filter((line) => line.id !== lineId);
            });
        },
        togglePassiveServiceFlow: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                const enabling = !invoice.serviceFlow;
                invoice.serviceFlow = enabling;

                if (enabling) {
                    invoice.selectedDdtIds = [];
                }
            });
        },
        addPassiveDirectLine: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                invoice.directLines = [
                    ...(invoice.directLines || []),
                    {
                        id: `direct-${invoiceId}-${Date.now()}`,
                        articleCode: null,
                        description: 'Nuova riga servizio',
                        groupId: 'utenze',
                        quantity: 1,
                        unitPrice: 0,
                        discount: 0,
                        vatRate: 22,
                    },
                ];
            });
        },
        removePassiveDirectLine: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId, lineId } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                invoice.directLines = (invoice.directLines || []).filter((line) => line.id !== lineId);
            });
        },
        promotePassiveSupplierFromXml: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                // Cerca prima un fornitore già in directory con la stessa P.IVA,
                // per evitare di generare un id diverso da quello già usato nei DDT.
                const existingByVat = (workspace.supplierDirectory || []).find(
                    (s) => s.vat === invoice.xmlSupplierVat
                );
                const supplierId = existingByVat?.id || invoice.supplierId || `sup-${invoice.xmlSupplierVat}`;

                if (!existingByVat) {
                    workspace.supplierDirectory = [
                        {
                            id: supplierId,
                            name: invoice.xmlSupplierName,
                            vat: invoice.xmlSupplierVat,
                            preferred: false,
                        },
                        ...(workspace.supplierDirectory || []),
                    ];
                }

                invoice.supplierKnown = true;
                invoice.supplierId = supplierId;
                invoice.supplierUnlinkedManually = false;
            });

            addNotification(state, {
                id: `notif-supplier-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Fornitore creato da XML',
                description: 'L’anagrafica fornitore è ora disponibile nel mock condiviso del ciclo passivo.',
                route: `/finance/passivo?invoice=${action.payload.invoiceId}`,
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        linkRealSupplierToInvoice: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId, supplierId, supplierName, supplierVat } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];
                if (!invoice) return;

                const existingIdx = (workspace.supplierDirectory || []).findIndex(
                    (s) => s.vat === supplierVat
                );
                if (existingIdx >= 0) {
                    workspace.supplierDirectory[existingIdx] = {
                        ...workspace.supplierDirectory[existingIdx],
                        id: supplierId,
                        name: supplierName,
                    };
                } else {
                    workspace.supplierDirectory = [
                        { id: supplierId, name: supplierName, vat: supplierVat, preferred: false },
                        ...(workspace.supplierDirectory || []),
                    ];
                }

                invoice.supplierKnown = true;
                invoice.supplierId = supplierId;
                invoice.supplierUnlinkedManually = false;
            });

            addNotification(state, {
                id: `notif-supplier-link-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Fornitore collegato',
                description: 'Il fornitore è stato collegato alla fattura dal ciclo passivo.',
                route: `/finance/passivo?invoice=${action.payload.invoiceId}`,
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        unlinkSupplierFromInvoice: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];
                if (!invoice) return;
                invoice.supplierKnown = false;
                invoice.supplierId = null;
                invoice.supplierUnlinkedManually = true;
            });
            persistState(state);
        },
        addImportedStatementLocally: (state, action) => {
            applyTreasuryWorkspaceMutation(state, (workspace) => {
                const { sourceId, fileName } = action.payload;
                const batchId = `import-${Date.now()}`;
                const today = dayjs().format('YYYY-MM-DD');

                workspace.imports = [
                    {
                        id: batchId,
                        sourceId,
                        fileName: fileName || `estratto-${today}.csv`,
                        importedAt: dayjs().toISOString(),
                        linesCount: 2,
                    },
                    ...(workspace.imports || []),
                ];

                workspace.bankMovements = [
                    {
                        id: `movement-${batchId}-1`,
                        sourceId,
                        companyDocumentId: 'aurifon',
                        date: today,
                        bookingDate: today,
                        description: 'Bonifico Energia Campania',
                        counterpartyName: 'Energia Campania',
                        amount: -503.52,
                        importedBatchId: batchId,
                        reconciledPayableId: null,
                    },
                    {
                        id: `movement-${batchId}-2`,
                        sourceId,
                        companyDocumentId: 'aurifon',
                        date: today,
                        bookingDate: today,
                        description: 'Addebito Google Workspace',
                        counterpartyName: 'Google',
                        amount: -114,
                        importedBatchId: batchId,
                        reconciledPayableId: null,
                        suspectedMissingDocument: true,
                    },
                    ...(workspace.bankMovements || []),
                ];
            });

            addNotification(state, {
                id: `notif-treasury-import-${Date.now()}`,
                level: 'info',
                unread: true,
                title: 'Estratto conto importato nel mock',
                description: 'Nuovi movimenti pronti per la riconciliazione automatica o manuale.',
                route: '/finance/tesoreria',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        registerTreasuryPaymentLocally: (state, action) => {
            const { payableId, sourceId, amount, date, valueDate, note, movementId } = action.payload;

            applyTreasuryWorkspaceMutation(state, (workspace) => {
                const payable = (workspace.payables || []).find((item) => item.id === payableId);
                const source = (workspace.sources || []).find((item) => item.id === sourceId);

                if (!payable || !source) {
                    return;
                }

                const safeAmount = Math.min(Number(amount || 0), Number(payable.residual || amount || 0));
                if (safeAmount <= 0) {
                    return;
                }

                const foreignAmount =
                    payable.foreignCurrency && payable.exchangeRate
                        ? Math.round((safeAmount / payable.exchangeRate + Number.EPSILON) * 100) / 100
                        : null;

                payable.paymentHistory = [
                    {
                        id: `payment-${Date.now()}`,
                        sourceId,
                        sourceLabel: source.name,
                        amount: safeAmount,
                        foreignAmount,
                        foreignCurrency: payable.foreignCurrency || null,
                        date: date || dayjs().format('YYYY-MM-DD'),
                        valueDate: valueDate || date || dayjs().format('YYYY-MM-DD'),
                        movementId: movementId || null,
                        note: note || 'Registrazione salda-conto mock',
                    },
                    ...(payable.paymentHistory || []),
                ];

                if (movementId) {
                    workspace.bankMovements = (workspace.bankMovements || []).map((movement) =>
                        movement.id === movementId
                            ? { ...movement, reconciledPayableId: payableId }
                            : movement
                    );
                } else {
                    workspace.bankMovements = [
                        {
                            id: `movement-manual-${Date.now()}`,
                            sourceId,
                            companyDocumentId: payable.companyDocumentId,
                            date: date || dayjs().format('YYYY-MM-DD'),
                            bookingDate: valueDate || date || dayjs().format('YYYY-MM-DD'),
                            description: `Pagamento ${payable.label}`,
                            counterpartyName: payable.supplierName,
                            amount: -Math.abs(safeAmount),
                            importedBatchId: null,
                            reconciledPayableId: payableId,
                        },
                        ...(workspace.bankMovements || []),
                    ];
                }
            });

            const linkedDocument = state.documents.find((document) => document.id === state.treasuryWorkspace?.payables?.find((item) => item.id === payableId)?.linkedDocumentId);
            if (linkedDocument) {
                linkedDocument.notes = [
                    {
                        id: `note-payment-${Date.now()}`,
                        createdAt: dayjs().toISOString(),
                        text: `Pagamento registrato via ${sourceId} per ${Number(amount || 0).toFixed(2)} €.`,
                    },
                    ...(linkedDocument.notes || []),
                ];
            }

            addNotification(state, {
                id: `notif-payment-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Pagamento registrato',
                description: 'Lo scadenzario e la riconciliazione sono stati aggiornati nel mock condiviso.',
                route: '/finance/tesoreria',
                companyDocumentId: linkedDocument?.companyDocumentId || state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        applyTreasuryCreditCompensationLocally: (state, action) => {
            const { payableId, creditId, amount } = action.payload;

            applyTreasuryWorkspaceMutation(state, (workspace) => {
                const payable = (workspace.payables || []).find((item) => item.id === payableId);
                const credit = (workspace.supplierCredits || []).find((item) => item.id === creditId);

                if (!payable || !credit) {
                    return;
                }

                const safeAmount = Math.min(
                    Number(amount || 0),
                    Number(credit.total || 0) - Number(credit.usedAmount || 0),
                    Number(payable.residual || amount || 0)
                );

                if (safeAmount <= 0) {
                    return;
                }

                credit.usedAmount = Number(credit.usedAmount || 0) + safeAmount;
                payable.appliedCredits = [
                    {
                        id: `applied-credit-${Date.now()}`,
                        creditId,
                        amount: safeAmount,
                        label: credit.label,
                        date: dayjs().format('YYYY-MM-DD'),
                    },
                    ...(payable.appliedCredits || []),
                ];
            });

            addNotification(state, {
                id: `notif-credit-${Date.now()}`,
                level: 'info',
                unread: true,
                title: 'Nota di credito compensata',
                description: 'Il saldo da pagare è stato ricalcolato usando il credito fornitore disponibile.',
                route: '/finance/tesoreria',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        importInventoryCatalogLocally: (state) => {
            applyInventoryWorkspaceMutation(state, (workspace) => {
                workspace.catalogImportedAt = dayjs().toISOString();
                workspace.lastInventoryReason = 'Fresh start con anagrafiche pulite';
            });

            addNotification(state, {
                id: `notif-inventory-import-${Date.now()}`,
                level: 'info',
                unread: true,
                title: 'Inventario iniziale pronto',
                description: 'Anagrafiche articoli attive e base magazzino pulita preparata per il nuovo start.',
                route: '/finance/magazzino',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        createInventoryTransferLocally: (state, action) => {
            const { fromDepositId, toDepositId, serialIds, operator, note } = action.payload;
            applyInventoryWorkspaceMutation(state, (workspace) => {
                const destination = (workspace.deposits || []).find((deposit) => deposit.id === toDepositId);
                const source = (workspace.deposits || []).find((deposit) => deposit.id === fromDepositId);
                const transferId = `transfer-${Date.now()}`;

                workspace.transfers = [
                    {
                        id: transferId,
                        date: dayjs().format('YYYY-MM-DD'),
                        fromDepositId,
                        toDepositId,
                        serialIds,
                        operator,
                        note,
                    },
                    ...(workspace.transfers || []),
                ];

                workspace.serials = (workspace.serials || []).map((serial) => {
                    if (!serialIds.includes(serial.id)) {
                        return serial;
                    }

                    return {
                        ...serial,
                        currentDepositId: toDepositId,
                        status: serial.status === 'in_prova' ? 'in_prova' : 'disponibile',
                        history: [
                            {
                                id: `history-${Date.now()}-${serial.id}`,
                                date: dayjs().format('YYYY-MM-DD'),
                                action: 'Trasferimento interno',
                                depositName: destination?.name || toDepositId,
                                operator,
                                note: `${source?.name || fromDepositId} -> ${destination?.name || toDepositId}${note ? ` · ${note}` : ''}`,
                            },
                            ...(serial.history || []),
                        ],
                    };
                });
            });

            addNotification(state, {
                id: `notif-transfer-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Trasferimento interno registrato',
                description: 'Depositi e pedigree matricola aggiornati nel mock magazzino.',
                route: '/finance/magazzino',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        startInventoryTrialLocally: (state, action) => {
            const { serialId, patientName, depositId, operator, note } = action.payload;
            applyInventoryWorkspaceMutation(state, (workspace) => {
                const destination = (workspace.deposits || []).find((deposit) => deposit.id === depositId);

                workspace.serials = (workspace.serials || []).map((serial) => {
                    if (serial.id !== serialId) {
                        return serial;
                    }

                    return {
                        ...serial,
                        currentDepositId: depositId,
                        status: 'in_prova',
                        patientName,
                        trialStartedAt: dayjs().format('YYYY-MM-DD'),
                        history: [
                            {
                                id: `history-${Date.now()}-${serial.id}`,
                                date: dayjs().format('YYYY-MM-DD'),
                                action: 'DDT contovisione',
                                depositName: destination?.name || depositId,
                                operator,
                                note: note || `Prova paziente ${patientName}`,
                            },
                            ...(serial.history || []),
                        ],
                    };
                });
            });

            addNotification(state, {
                id: `notif-trial-${Date.now()}`,
                level: 'info',
                unread: true,
                title: 'Contovisione attivata',
                description: `La matricola è ora fuori dal deposito centrale e associata a ${patientName}.`,
                route: '/finance/magazzino',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        consumeInventoryMaterialLocally: (state, action) => {
            const { articleCode, depositId, quantity, operator, note } = action.payload;
            applyInventoryWorkspaceMutation(state, (workspace) => {
                workspace.consumptions = [
                    {
                        id: `cons-${Date.now()}`,
                        articleCode,
                        depositId,
                        quantity: Number(quantity || 0),
                        date: dayjs().format('YYYY-MM-DD'),
                        operator,
                        note,
                    },
                    ...(workspace.consumptions || []),
                ];
            });

            addNotification(state, {
                id: `notif-consume-${Date.now()}`,
                level: 'warning',
                unread: true,
                title: 'Consumo laboratorio registrato',
                description: 'Le giacenze dei materiali minori sono state aggiornate nel mock.',
                route: '/finance/magazzino',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        generateInventoryReorderLocally: (state, action) => {
            const { articleCode } = action.payload;
            applyInventoryWorkspaceMutation(state, (workspace) => {
                const article = (workspace.articleStock || workspace.articles || []).find((item) => item.code === articleCode);
                if (!article) {
                    return;
                }

                workspace.reorderProposals = [
                    {
                        id: `reorder-${Date.now()}`,
                        articleCode,
                        articleLabel: article.label,
                        supplierName: article.preferredSupplier || article.supplierName,
                        suggestedQty: Math.max(Number(article.minStock || 0) * 2 - Number(article.effectiveQty || 0), 1),
                        createdAt: dayjs().toISOString(),
                    },
                    ...(workspace.reorderProposals || []),
                ];
            });

            addNotification(state, {
                id: `notif-reorder-${Date.now()}`,
                level: 'warning',
                unread: true,
                title: 'Proposta ordine fornitore pronta',
                description: 'L’articolo sotto scorta è stato aggiunto alla bozza ordine mock.',
                route: '/finance/magazzino',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        startMockSave: (state, action) => {
            state.pendingSaves = [
                {
                    ...action.payload,
                    status: 'saving',
                    createdAt: dayjs().toISOString(),
                },
                ...state.pendingSaves.filter((save) => save.id !== action.payload.id),
            ];
            persistState(state);
        },
        queueMockSave: (state, action) => {
            state.pendingSaves = state.pendingSaves.map((save) =>
                save.id === action.payload.id ? { ...save, status: 'queued' } : save
            );
            addNotification(state, {
                id: `notif-${Date.now()}`,
                level: 'warning',
                unread: true,
                title: 'Salvataggio messo in coda',
                description: 'La modifica resta locale e verrà sincronizzata quando torni online.',
                route: '/finance/shell',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        markMockSaveSaving: (state, action) => {
            state.pendingSaves = state.pendingSaves.map((save) =>
                save.id === action.payload.id ? { ...save, status: 'saving' } : save
            );
            persistState(state);
        },
        completeMockSave: (state, action) => {
            state.pendingSaves = state.pendingSaves.filter((save) => save.id !== action.payload.id);
            state.lastSyncedAt = dayjs().toISOString();
            persistState(state);
        },
        commitPassiveInvoiceLocally: (state, action) => {            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { invoiceId } = action.payload;
                const invoice = workspace.invoices?.[invoiceId];

                if (!invoice) {
                    return;
                }

                invoice.reviewStatus = 'contabilizzata';
                invoice.protocolAssigned = invoice.computed?.protocolPreview || `Registro ${invoice.protocolRegister || 1} / Protocollo ${invoice.protocolNumber || 'simulato'}`;
                invoice.lastCommittedAt = dayjs().toISOString();

                workspace.ddts = (workspace.ddts || []).map((ddt) =>
                    invoice.selectedDdtIds.includes(ddt.id)
                        ? { ...ddt, status: 'fatturata' }
                        : ddt
                );
            });

            const document = state.documents.find((item) => item.id === action.payload.invoiceId);
            if (document) {
                document.status = 'contabilizzata';
                document.updatedAt = dayjs().toISOString();
                document.notes = [
                    {
                        id: `note-${Date.now()}`,
                        createdAt: dayjs().toISOString(),
                        text: 'Contabilizzazione mock completata dal workspace passivo.',
                    },
                    ...(document.notes || []),
                ];
            }

            addNotification(state, {
                id: `notif-passive-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Fattura passiva contabilizzata',
                description: 'Protocollo IVA assegnato e DDT correlati aggiornati a Fatturata.',
                route: `/finance/passivo?invoice=${action.payload.invoiceId}`,
                companyDocumentId: document?.companyDocumentId || state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        setActiveForeignInvoice: (state, action) => {
            if (state.foreignInvoiceWorkspace) {
                state.foreignInvoiceWorkspace.activeForeignInvoiceId = action.payload;
                persistState(state);
            }
        },
        addForeignInvoiceLocally: (state, action) => {
            applyForeignInvoiceWorkspaceMutation(state, (workspace) => {
                const invoice = {
                    id: `foreign-inv-${Date.now()}`,
                    referenceNumber: '',
                    supplierName: '',
                    supplierCountry: '',
                    supplierVat: '',
                    currency: 'EUR',
                    issueDate: dayjs().format('YYYY-MM-DD'),
                    totalAmountForeign: 0,
                    exchangeRate: 1,
                    totalAmountEur: 0,
                    vatRegime: 'inversione_contabile',
                    purchaseAccount: 'Merci c/acquisti',
                    costCenter: 'Aurifon HQ',
                    companyDocumentId: action.payload?.companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                    linkedCustomsDeclarationIds: [],
                    status: 'bozza',
                    notes: '',
                    createdAt: dayjs().toISOString(),
                };
                workspace.foreignInvoices = [invoice, ...(workspace.foreignInvoices || [])];
                workspace.activeForeignInvoiceId = invoice.id;
            });
        },
        updateForeignInvoiceLocally: (state, action) => {
            applyForeignInvoiceWorkspaceMutation(state, (workspace) => {
                const { invoiceId, changes } = action.payload;
                const index = (workspace.foreignInvoices || []).findIndex((inv) => inv.id === invoiceId);

                if (index === -1) {
                    return;
                }

                const updated = { ...workspace.foreignInvoices[index], ...changes };
                if (changes.totalAmountForeign !== undefined || changes.exchangeRate !== undefined) {
                    const amount = Number(changes.totalAmountForeign ?? workspace.foreignInvoices[index].totalAmountForeign ?? 0);
                    const rate = Number(changes.exchangeRate ?? workspace.foreignInvoices[index].exchangeRate ?? 1);
                    updated.totalAmountEur = Math.round((amount * rate + Number.EPSILON) * 100) / 100;
                }

                workspace.foreignInvoices[index] = updated;
            });
        },
        addCustomsDeclarationLocally: (state, action) => {
            applyForeignInvoiceWorkspaceMutation(state, (workspace) => {
                const { invoiceId } = action.payload;
                const declarationId = `customs-${Date.now()}`;
                const declaration = {
                    id: declarationId,
                    declarationNumber: '',
                    declarationDate: dayjs().format('YYYY-MM-DD'),
                    portOfEntry: '',
                    goodsDescription: '',
                    customsValue: 0,
                    dutiesAmount: 0,
                    vatAmount: 0,
                    clearanceCosts: [],
                    linkedInvoiceId: invoiceId || null,
                };
                workspace.customsDeclarations = [...(workspace.customsDeclarations || []), declaration];

                if (invoiceId) {
                    const invoice = (workspace.foreignInvoices || []).find((inv) => inv.id === invoiceId);
                    if (invoice) {
                        invoice.linkedCustomsDeclarationIds = [...(invoice.linkedCustomsDeclarationIds || []), declarationId];
                    }
                }
            });
        },
        updateCustomsDeclarationLocally: (state, action) => {
            applyForeignInvoiceWorkspaceMutation(state, (workspace) => {
                const { declarationId, changes } = action.payload;
                const index = (workspace.customsDeclarations || []).findIndex((dec) => dec.id === declarationId);

                if (index !== -1) {
                    workspace.customsDeclarations[index] = { ...workspace.customsDeclarations[index], ...changes };
                }
            });
        },
        addCustomsCostLocally: (state, action) => {
            applyForeignInvoiceWorkspaceMutation(state, (workspace) => {
                const { declarationId } = action.payload;
                const declaration = (workspace.customsDeclarations || []).find((dec) => dec.id === declarationId);

                if (declaration) {
                    declaration.clearanceCosts = [
                        ...(declaration.clearanceCosts || []),
                        {
                            id: `cc-${Date.now()}`,
                            description: '',
                            amount: 0,
                        },
                    ];
                }
            });
        },
        removeCustomsCostLocally: (state, action) => {
            applyForeignInvoiceWorkspaceMutation(state, (workspace) => {
                const { declarationId, costId } = action.payload;
                const declaration = (workspace.customsDeclarations || []).find((dec) => dec.id === declarationId);

                if (declaration) {
                    declaration.clearanceCosts = (declaration.clearanceCosts || []).filter((cost) => cost.id !== costId);
                }
            });
        },
        updateCustomsCostLocally: (state, action) => {
            applyForeignInvoiceWorkspaceMutation(state, (workspace) => {
                const { declarationId, costId, changes } = action.payload;
                const declaration = (workspace.customsDeclarations || []).find((dec) => dec.id === declarationId);

                if (declaration) {
                    declaration.clearanceCosts = (declaration.clearanceCosts || []).map((cost) =>
                        cost.id === costId ? { ...cost, ...changes } : cost
                    );
                }
            });
        },
        commitForeignInvoiceLocally: (state, action) => {
            const { invoiceId } = action.payload;
            let committedInvoice = null;

            applyForeignInvoiceWorkspaceMutation(state, (workspace) => {
                const invoice = (workspace.foreignInvoices || []).find((inv) => inv.id === invoiceId);

                if (invoice) {
                    invoice.status = 'contabilizzata';
                    invoice.committedAt = dayjs().toISOString();
                    committedInvoice = invoice;
                }
            });

            if (committedInvoice) {
                applyTreasuryWorkspaceMutation(state, (workspace) => {
                    const exists = (workspace.payables || []).some((p) => p.foreignInvoiceId === invoiceId);

                    if (!exists) {
                        const issueDate = committedInvoice.issueDate || dayjs().format('YYYY-MM-DD');
                        workspace.payables = [
                            {
                                id: `payable-foreign-${invoiceId}`,
                                foreignInvoiceId: invoiceId,
                                companyDocumentId: committedInvoice.companyDocumentId,
                                supplierName: committedInvoice.supplierName,
                                label: `Fattura estera ${committedInvoice.referenceNumber || invoiceId}`,
                                total: committedInvoice.totalAmountEur,
                                foreignCurrency: committedInvoice.currency !== 'EUR' ? committedInvoice.currency : null,
                                foreignAmount: committedInvoice.currency !== 'EUR' ? committedInvoice.totalAmountForeign : null,
                                exchangeRate: committedInvoice.currency !== 'EUR' ? committedInvoice.exchangeRate : null,
                                issueDate,
                                dueDate: dayjs(issueDate).add(30, 'day').format('YYYY-MM-DD'),
                                year: dayjs(issueDate).year(),
                                sourceKind: 'foreign_invoice',
                                creditIds: [],
                                paymentHistory: [],
                                appliedCredits: [],
                            },
                            ...(workspace.payables || []),
                        ];
                    }
                });
            }

            addNotification(state, {
                id: `notif-foreign-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Fattura estera contabilizzata',
                description: 'La fattura estera è stata contabilizzata con regime IVA applicato.',
                route: '/finance/fattura-estera',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
        },
        applyDocumentTransition: (state, action) => {
            const { documentId, nextStatus, note } = action.payload;
            const document = state.documents.find((item) => item.id === documentId);

            if (!document) {
                return;
            }

            document.status = nextStatus;
            document.updatedAt = dayjs().toISOString();
            document.notes = [
                {
                    id: `note-${Date.now()}`,
                    createdAt: dayjs().toISOString(),
                    text: note || `Stato aggiornato a ${getDocumentStatusLabel(nextStatus)}`,
                },
                ...(document.notes || []),
            ];

            addNotification(state, {
                id: `notif-${Date.now()}-${documentId}`,
                level: nextStatus === 'fatturata' || nextStatus === 'contabilizzata' ? 'success' : 'info',
                unread: true,
                title: `${document.title} aggiornato`,
                description: `Nuovo stato: ${getDocumentStatusLabel(nextStatus)}`,
                route: `/finance/documentale?doc=${documentId}`,
                companyDocumentId: document.companyDocumentId,
                createdAt: dayjs().toISOString(),
            });

            persistState(state);
        },
        createReturnDdtLocally: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { originalDdtId } = action.payload;
                const sourceDdt = (workspace.ddts || []).find((ddt) => ddt.id === originalDdtId);

                if (!sourceDdt) {
                    return;
                }

                const alreadyExists = (workspace.returnDdts || []).some((r) => r.originalDdtId === originalDdtId);
                if (alreadyExists) {
                    return;
                }

                const returnId = `return-${originalDdtId}`;
                workspace.returnDdts = [
                    {
                        id: returnId,
                        originalDdtId,
                        supplierId: sourceDdt.supplierId,
                        companyDocumentId: sourceDdt.companyDocumentId,
                        internalNumber: `RES-${sourceDdt.internalNumber}`,
                        returnedAt: dayjs().format('YYYY-MM-DD'),
                        status: 'bozza',
                        lines: sourceDdt.lines.map((line) => ({
                            ...line,
                            id: `${returnId}-${line.id}`,
                        })),
                        linkedCreditNoteId: null,
                        note: '',
                    },
                    ...(workspace.returnDdts || []),
                ];
            });

            addNotification(state, {
                id: `notif-return-create-${Date.now()}`,
                level: 'info',
                unread: true,
                title: 'Reso fornitore creato',
                description: 'Il DDT di ritorno è in bozza. Verifica le righe e invialo al fornitore.',
                route: '/finance/reso-fornitore',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        updateReturnDdtLineLocally: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { returnDdtId, lineId, changes } = action.payload;
                const returnDdt = (workspace.returnDdts || []).find((r) => r.id === returnDdtId);

                if (!returnDdt || returnDdt.status !== 'bozza') {
                    return;
                }

                returnDdt.lines = returnDdt.lines.map((line) =>
                    line.id === lineId ? { ...line, ...changes } : line
                );
            });
        },
        updateReturnDdtNoteLocally: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { returnDdtId, note } = action.payload;
                const returnDdt = (workspace.returnDdts || []).find((r) => r.id === returnDdtId);

                if (!returnDdt) {
                    return;
                }

                returnDdt.note = note;
            });
        },
        submitReturnDdtLocally: (state, action) => {
            applyPassiveWorkspaceMutation(state, (workspace) => {
                const { returnDdtId } = action.payload;
                const returnDdt = (workspace.returnDdts || []).find((r) => r.id === returnDdtId);

                if (!returnDdt || returnDdt.status !== 'bozza') {
                    return;
                }

                returnDdt.status = 'inviato';
                returnDdt.submittedAt = dayjs().toISOString();
            });

            addNotification(state, {
                id: `notif-return-submit-${Date.now()}`,
                level: 'info',
                unread: true,
                title: 'DDT di ritorno inviato al fornitore',
                description: 'In attesa della nota di credito passiva per chiudere la pendenza.',
                route: '/finance/reso-fornitore',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        linkCreditNoteToReturnLocally: (state, action) => {
            const { returnDdtId, creditNoteReference, creditNoteDate, creditNoteTotal } = action.payload;
            const creditId = `credit-return-${returnDdtId}`;

            applyPassiveWorkspaceMutation(state, (workspace) => {
                const returnDdt = (workspace.returnDdts || []).find((r) => r.id === returnDdtId);

                if (!returnDdt || returnDdt.linkedCreditNoteId) {
                    return;
                }

                returnDdt.linkedCreditNoteId = creditId;
                returnDdt.status = 'chiuso';
                returnDdt.closedAt = dayjs().toISOString();
            });

            applyTreasuryWorkspaceMutation(state, (workspace) => {
                const passiveWorkspace = state.passiveWorkspace;
                const returnDdt = (passiveWorkspace?.returnDdts || []).find((r) => r.id === returnDdtId);

                if (!returnDdt) {
                    return;
                }

                const supplier = (passiveWorkspace?.supplierDirectory || []).find((s) => s.id === returnDdt.supplierId);

                workspace.supplierCredits = [
                    {
                        id: creditId,
                        companyDocumentId: returnDdt.companyDocumentId,
                        supplierName: supplier?.name || returnDdt.supplierId,
                        label: `Nota di credito fornitore ${creditNoteReference}`,
                        total: Number(creditNoteTotal || 0),
                        usedAmount: 0,
                        issuedAt: creditNoteDate || dayjs().format('YYYY-MM-DD'),
                        year: dayjs(creditNoteDate || undefined).year(),
                        linkedReturnDdtId: returnDdtId,
                    },
                    ...(workspace.supplierCredits || []),
                ];
            });

            addNotification(state, {
                id: `notif-credit-return-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Nota di credito passiva registrata',
                description: `${creditNoteReference} · ${Number(creditNoteTotal || 0).toFixed(2)} € disponibili per compensazione scadenze.`,
                route: '/finance/reso-fornitore',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        setActiveAgreementId: (state, action) => {
            if (state.activeWorkspace) {
                state.activeWorkspace.activeAgreementId = action.payload;
                persistState(state);
            }
        },
        setActiveQueueItemId: (state, action) => {
            if (state.activeWorkspace) {
                state.activeWorkspace.activeQueueItemId = action.payload;
                persistState(state);
            }
        },
        setActiveCreditNoteId: (state, action) => {
            if (state.activeWorkspace) {
                state.activeWorkspace.activeCreditNoteId = action.payload;
                persistState(state);
            }
        },
        addActiveAgreementLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { companyDocumentId, patientName } = action.payload || {};
                const agreementId = `agreement-${Date.now()}`;
                workspace.agreements = [
                    {
                        id: agreementId,
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        patientName: patientName || '',
                        patientCode: '',
                        serialId: null,
                        serialNumber: null,
                        articleCode: null,
                        articleLabel: '',
                        dealType: 'privato',
                        price: 0,
                        discount: 0,
                        aslQuota: 0,
                        privateQuota: 0,
                        paymentMethod: 'contanti',
                        installments: [],
                        status: 'bozza',
                        linkedDocumentId: null,
                        createdAt: dayjs().toISOString(),
                        confirmedAt: null,
                        notes: '',
                    },
                    ...(workspace.agreements || []),
                ];
                workspace.activeAgreementId = agreementId;
            });
        },
        updateActiveAgreementLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { agreementId, changes } = action.payload;
                const index = (workspace.agreements || []).findIndex((a) => a.id === agreementId);
                if (index !== -1) {
                    workspace.agreements[index] = { ...workspace.agreements[index], ...changes };
                }
            });
        },
        confirmActiveAgreementLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { agreementId } = action.payload;
                const agreement = (workspace.agreements || []).find((a) => a.id === agreementId);
                if (agreement && agreement.status === 'bozza') {
                    agreement.status = 'confermato';
                    agreement.confirmedAt = dayjs().toISOString();
                }
            });

            const agreement = (state.activeWorkspace?.agreements || []).find((a) => a.id === action.payload.agreementId);
            addNotification(state, {
                id: `notif-agreement-confirm-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Accordo confermato',
                description: `L'accordo con ${agreement?.patientName || 'il paziente'} è stato confermato.`,
                route: '/finance/attivo',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        sendActiveAgreementToAdminLocally: (state, action) => {
            const { agreementId, note } = action.payload;
            applyActiveWorkspaceMutation(state, (workspace) => {
                const agreement = (workspace.agreements || []).find((a) => a.id === agreementId);
                if (agreement) {
                    if (agreement.status === 'bozza') {
                        agreement.status = 'confermato';
                        agreement.confirmedAt = dayjs().toISOString();
                    }
                    agreement.status = 'inviato_amministrazione';
                }

                const queueId = `queue-${agreementId}-${Date.now()}`;
                workspace.operativeQueue = [
                    {
                        id: queueId,
                        companyDocumentId: agreement?.companyDocumentId || state.activeCompanyDocumentId,
                        title: `${agreement?.patientName || 'Paziente'} – accordo da fatturare`,
                        patientName: agreement?.patientName || null,
                        amount: agreement?.privateQuota || agreement?.price || 0,
                        priority: 'normale',
                        status: 'in_attesa',
                        fromRole: 'commerciale',
                        toRole: 'contabile',
                        linkedAgreementId: agreementId,
                        linkedDocumentId: agreement?.linkedDocumentId || null,
                        note: note || 'Accordo inviato in amministrazione per fatturazione.',
                        createdAt: dayjs().toISOString(),
                    },
                    ...(workspace.operativeQueue || []),
                ];
            });

            const agreement = (state.activeWorkspace?.agreements || []).find((a) => a.id === agreementId);
            addNotification(state, {
                id: `notif-agreement-admin-${Date.now()}`,
                level: 'info',
                unread: true,
                title: 'Accordo inviato in amministrazione',
                description: `${agreement?.patientName || 'Paziente'} – pratica in coda operativa per contabile.`,
                route: '/finance/attivo',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        pushPatientAgreementToQueueLocally: (state, action) => {
            const { patientId, patientName, amount, note, priority, companyDocumentId, pianoScadenze } = action.payload;
            const queueId = `queue-patient-${patientId}-${Date.now()}`;
            applyActiveWorkspaceMutation(state, (workspace) => {
                workspace.operativeQueue = [
                    {
                        id: queueId,
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId,
                        title: `${patientName || 'Paziente'} – pratica da fatturare`,
                        patientName: patientName || null,
                        amount: amount || 0,
                        priority: priority || 'normale',
                        status: 'in_attesa',
                        fromRole: 'commerciale',
                        toRole: 'contabile',
                        linkedAgreementId: null,
                        linkedDocumentId: null,
                        note: note || 'Accordo commerciale chiuso. Pratica inviata in fatturazione.',
                        pianoScadenze: pianoScadenze || [],
                        createdAt: dayjs().toISOString(),
                    },
                    ...(workspace.operativeQueue || []),
                ];
            });

            addNotification(state, {
                id: `notif-patient-billing-${Date.now()}`,
                level: 'info',
                unread: true,
                title: 'Pratica inviata in fatturazione',
                description: `${patientName || 'Paziente'} – accordo chiuso dal commerciale. Pratica in coda da fatturare.`,
                route: '/finance/coda-pratiche-fatturare',
                companyDocumentId: companyDocumentId || state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        markQueueItemCompleteLocally: (state, action) => {
            const { queueItemId } = action.payload;
            applyActiveWorkspaceMutation(state, (workspace) => {
                const item = (workspace.operativeQueue || []).find((q) => q.id === queueItemId);
                if (item) {
                    item.status = 'completato';
                    item.completedAt = dayjs().toISOString();
                }

                const linkedAgreement = item?.linkedAgreementId
                    ? (workspace.agreements || []).find((a) => a.id === item.linkedAgreementId)
                    : null;
                if (linkedAgreement && linkedAgreement.status === 'inviato_amministrazione') {
                    linkedAgreement.status = 'fatturato';
                }
            });

            addNotification(state, {
                id: `notif-queue-done-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Pratica fatturata',
                description: 'L\'elemento è stato marcato come completato dal contabile.',
                route: '/finance/coda-pratiche-fatturare',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        updateQueueItemStatusLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { queueItemId, status, note } = action.payload;
                const item = (workspace.operativeQueue || []).find((q) => q.id === queueItemId);
                if (item) {
                    item.status = status;
                    if (note) {
                        item.note = note;
                    }
                }
            });
        },
        addActiveCreditNoteFromInvoiceLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { companyDocumentId, referenceInvoiceId, referenceInvoiceNumber, recipientName, reason, lines } = action.payload || {};
                const creditNoteId = `credit-note-active-${Date.now()}`;
                const ts = Date.now();
                const newLines = (lines || []).map((l, idx) => {
                    const qty = Number(l.quantity) || 0;
                    const price = Number(l.unitPrice) || 0;
                    const vat = Number(l.vatRate) || 22;
                    const vatAmt = Number((qty * price * (vat / 100)).toFixed(2));
                    const lineTotal = Number((qty * price + vatAmt).toFixed(2));
                    return {
                        id: `cn-line-${ts}-${idx}`,
                        description: l.description || '',
                        quantity: qty,
                        unitPrice: price,
                        vatRate: vat,
                        vatAmount: vatAmt,
                        lineTotal,
                    };
                });
                const amount = Number(newLines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0).toFixed(2));
                const vatAmount = Number(newLines.reduce((sum, l) => sum + l.vatAmount, 0).toFixed(2));
                const total = Number((amount + vatAmount).toFixed(2));
                workspace.creditNotes = [
                    {
                        id: creditNoteId,
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        referenceInvoiceId: referenceInvoiceId || null,
                        referenceInvoiceNumber: referenceInvoiceNumber || '',
                        patientName: recipientName || '',
                        reason: reason || '',
                        lines: newLines,
                        amount,
                        vatRate: 22,
                        vatAmount,
                        total,
                        status: 'bozza',
                        emittedAt: null,
                        linkedReInvoiceId: null,
                        notes: '',
                    },
                    ...(workspace.creditNotes || []),
                ];
                workspace.activeCreditNoteId = creditNoteId;
            });
        },
        addActiveCreditNoteLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { companyDocumentId, patientName } = action.payload || {};
                const creditNoteId = `credit-note-active-${Date.now()}`;
                workspace.creditNotes = [
                    {
                        id: creditNoteId,
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        referenceInvoiceId: null,
                        referenceInvoiceNumber: '',
                        patientName: patientName || '',
                        reason: '',
                        lines: [
                            {
                                id: `cn-line-${Date.now()}`,
                                description: '',
                                quantity: 1,
                                unitPrice: 0,
                                vatRate: 22,
                                vatAmount: 0,
                                lineTotal: 0,
                            },
                        ],
                        amount: 0,
                        vatRate: 22,
                        vatAmount: 0,
                        total: 0,
                        status: 'bozza',
                        emittedAt: null,
                        linkedReInvoiceId: null,
                        notes: '',
                    },
                    ...(workspace.creditNotes || []),
                ];
                workspace.activeCreditNoteId = creditNoteId;
            });
        },
        updateActiveCreditNoteLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { creditNoteId, changes } = action.payload;
                const index = (workspace.creditNotes || []).findIndex((cn) => cn.id === creditNoteId);
                if (index !== -1) {
                    workspace.creditNotes[index] = { ...workspace.creditNotes[index], ...changes };
                }
            });
        },
        emitActiveCreditNoteLocally: (state, action) => {
            const { creditNoteId } = action.payload;

            const creditNote = (state.activeWorkspace?.creditNotes || []).find((cn) => cn.id === creditNoteId);
            if (!creditNote || creditNote.status !== 'bozza') return;

            const refInvoiceId = creditNote.referenceInvoiceId;
            const refInvoice = refInvoiceId
                ? (state.billingWorkspace?.b2bInvoices || []).find((inv) => inv.id === refInvoiceId)
                    || (state.billingWorkspace?.freeInvoices || []).find((inv) => inv.id === refInvoiceId)
                : null;
            const serialIdsToRestore = (refInvoice?.lines || []).map((l) => l.serialId).filter(Boolean);

            applyActiveWorkspaceMutation(state, (workspace) => {
                const cn = (workspace.creditNotes || []).find((c) => c.id === creditNoteId);
                if (!cn || cn.status !== 'bozza') return;
                cn.status = 'emessa';
                cn.emittedAt = dayjs().format('YYYY-MM-DD');

                const recipientName = cn.patientName || '';
                const cnTotal = cn.total || 0;
                if (recipientName && cnTotal > 0) {
                    const companyId = cn.companyDocumentId || state.activeCompanyDocumentId || 'aurifon';
                    let ledger = (workspace.customerLedgers || []).find(
                        (l) => l.patientName === recipientName && l.companyDocumentId === companyId
                    );
                    if (!ledger) {
                        const newLedger = {
                            id: `ledger-storno-${Date.now()}`,
                            companyDocumentId: companyId,
                            patientName: recipientName,
                            patientCode: '',
                            entries: [],
                            openBalance: 0,
                        };
                        workspace.customerLedgers = [newLedger, ...(workspace.customerLedgers || [])];
                        ledger = newLedger;
                    }
                    const entries = ledger.entries || [];
                    const prevBalance = entries.length > 0 ? entries[entries.length - 1].balance : (ledger.openBalance || 0);
                    const newBalance = Math.round((prevBalance - cnTotal) * 100) / 100;
                    ledger.entries = [
                        ...entries,
                        {
                            id: `entry-storno-${creditNoteId}`,
                            date: dayjs().format('YYYY-MM-DD'),
                            description: `NC ${cn.referenceInvoiceNumber || 'storno'} – ${cn.reason || 'Storno fattura'}`,
                            debit: 0,
                            credit: cnTotal,
                            balance: newBalance,
                        },
                    ];
                }
            });

            if (refInvoiceId) {
                applyBillingWorkspaceMutation(state, (workspace) => {
                    const b2bInv = (workspace.b2bInvoices || []).find((inv) => inv.id === refInvoiceId);
                    if (b2bInv && !['annullata', 'stornata'].includes(b2bInv.status)) {
                        b2bInv.status = 'annullata';
                        b2bInv.stornataCreditNoteId = creditNoteId;
                        b2bInv.stornataAt = dayjs().format('YYYY-MM-DD');
                    }
                    const freeInv = (workspace.freeInvoices || []).find((inv) => inv.id === refInvoiceId);
                    if (freeInv && !['annullata', 'stornata'].includes(freeInv.status)) {
                        freeInv.status = 'annullata';
                        freeInv.stornataCreditNoteId = creditNoteId;
                        freeInv.stornataAt = dayjs().format('YYYY-MM-DD');
                    }
                });
            }

            if (serialIdsToRestore.length > 0) {
                applyInventoryWorkspaceMutation(state, (workspace) => {
                    workspace.serials = (workspace.serials || []).map((serial) => {
                        if (!serialIdsToRestore.includes(serial.id) || serial.status !== 'venduto') return serial;
                        return {
                            ...serial,
                            status: 'disponibile',
                            history: [
                                {
                                    id: `history-storno-${Date.now()}-${serial.id}`,
                                    date: dayjs().format('YYYY-MM-DD'),
                                    type: 'storno',
                                    note: `Ripristino disponibilità da storno fattura ${creditNote.referenceInvoiceNumber || refInvoiceId}`,
                                },
                                ...(serial.history || []),
                            ],
                        };
                    });
                });
            }

            addNotification(state, {
                id: `notif-credit-active-${Date.now()}`,
                level: 'warning',
                unread: true,
                title: 'Nota di credito attiva emessa',
                description: `Storno registrato: prima nota aggiornata${refInvoiceId ? ', fattura annullata nello scadenzario' : ''}${serialIdsToRestore.length > 0 ? `, ${serialIdsToRestore.length === 1 ? 'matricola ripristinata' : 'matricole ripristinate'} come disponibili` : ''}. Verificare se è necessaria una rifatturazione.`,
                route: '/finance/attivo',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        addAssistedReInvoiceFromCreditNoteLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { linkedCreditNoteId, newIssueDate, paymentTerms, notes } = action.payload || {};
                const creditNote = (workspace.creditNotes || []).find((cn) => cn.id === linkedCreditNoteId);
                if (!creditNote) return;
                const reInvoiceId = `re-invoice-${Date.now()}`;
                const ts = Date.now();
                const newLines = (creditNote.lines || []).map((l, idx) => {
                    const qty = Number(l.quantity) || 0;
                    const price = Number(l.unitPrice) || 0;
                    const vat = Number(l.vatRate) || 22;
                    const vatAmt = Number((qty * price * (vat / 100)).toFixed(2));
                    const lineTotal = Number((qty * price + vatAmt).toFixed(2));
                    return {
                        id: `ri-line-${ts}-${idx}`,
                        description: l.description || '',
                        quantity: qty,
                        unitPrice: price,
                        vatRate: vat,
                        vatAmount: vatAmt,
                        lineTotal,
                    };
                });
                const amount = Number(newLines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0).toFixed(2));
                const vatAmount = Number(newLines.reduce((sum, l) => sum + l.vatAmount, 0).toFixed(2));
                const total = Number((amount + vatAmount).toFixed(2));
                workspace.reInvoices = [
                    {
                        id: reInvoiceId,
                        companyDocumentId: creditNote.companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        linkedCreditNoteId: linkedCreditNoteId || null,
                        referenceInvoiceNumber: creditNote.referenceInvoiceNumber || '',
                        patientName: creditNote.patientName || '',
                        reason: `Rifatturazione di ${creditNote.referenceInvoiceNumber || 'fattura stornata'}`,
                        issueDate: newIssueDate || dayjs().format('YYYY-MM-DD'),
                        paymentTerms: paymentTerms || null,
                        lines: newLines,
                        amount,
                        vatRate: 22,
                        vatAmount,
                        total,
                        status: 'bozza',
                        emittedAt: null,
                        notes: notes || '',
                    },
                    ...(workspace.reInvoices || []),
                ];
                creditNote.linkedReInvoiceId = reInvoiceId;
            });
        },
        addActiveReInvoiceLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { companyDocumentId, patientName, linkedCreditNoteId } = action.payload || {};
                const reInvoiceId = `re-invoice-${Date.now()}`;
                workspace.reInvoices = [
                    {
                        id: reInvoiceId,
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        linkedCreditNoteId: linkedCreditNoteId || null,
                        patientName: patientName || '',
                        reason: '',
                        lines: [
                            {
                                id: `ri-line-${Date.now()}`,
                                description: '',
                                quantity: 1,
                                unitPrice: 0,
                                vatRate: 22,
                                vatAmount: 0,
                                lineTotal: 0,
                            },
                        ],
                        amount: 0,
                        vatRate: 22,
                        vatAmount: 0,
                        total: 0,
                        status: 'bozza',
                        emittedAt: null,
                        notes: '',
                    },
                    ...(workspace.reInvoices || []),
                ];

                if (linkedCreditNoteId) {
                    const creditNote = (workspace.creditNotes || []).find((cn) => cn.id === linkedCreditNoteId);
                    if (creditNote) {
                        creditNote.linkedReInvoiceId = reInvoiceId;
                    }
                }
            });
        },
        updateActiveReInvoiceLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { reInvoiceId, changes } = action.payload;
                const index = (workspace.reInvoices || []).findIndex((ri) => ri.id === reInvoiceId);
                if (index !== -1) {
                    workspace.reInvoices[index] = { ...workspace.reInvoices[index], ...changes };
                }
            });
        },
        emitActiveReInvoiceLocally: (state, action) => {
            const { reInvoiceId } = action.payload;
            applyActiveWorkspaceMutation(state, (workspace) => {
                const reInvoice = (workspace.reInvoices || []).find((ri) => ri.id === reInvoiceId);
                if (reInvoice && reInvoice.status === 'bozza') {
                    reInvoice.status = 'emessa';
                    reInvoice.emittedAt = dayjs().format('YYYY-MM-DD');
                }
            });

            addNotification(state, {
                id: `notif-reinvoice-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Rifatturazione emessa',
                description: 'La rifatturazione è stata emessa con successo.',
                route: '/finance/attivo',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        addActiveReceiptLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { companyDocumentId, receiptType } = action.payload || {};
                const resolvedType = receiptType || 'smart_receipt';
                const receiptId = `receipt-${Date.now()}`;
                workspace.receipts = [
                    {
                        id: receiptId,
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        receiptType: resolvedType,
                        date: dayjs().format('YYYY-MM-DD'),
                        lines: [
                            {
                                id: `rec-line-${Date.now()}`,
                                description: '',
                                quantity: 1,
                                unitPrice: 0,
                                vatRate: 22,
                            },
                        ],
                        paymentMethod: 'contanti',
                        amount: 0,
                        vatAmount: 0,
                        total: 0,
                        customerName: '',
                        notes: '',
                        status: 'bozza',
                        emittedAt: null,
                        sdiTransmission: { status: resolvedType === 'smart_receipt' ? 'pending' : 'not_required', transmittedAt: null },
                    },
                    ...(workspace.receipts || []),
                ];
            });
        },
        updateActiveReceiptLocally: (state, action) => {
            applyActiveWorkspaceMutation(state, (workspace) => {
                const { receiptId, changes } = action.payload;
                const index = (workspace.receipts || []).findIndex((r) => r.id === receiptId);
                if (index !== -1) {
                    workspace.receipts[index] = { ...workspace.receipts[index], ...changes };
                }
            });
        },
        emitActiveReceiptLocally: (state, action) => {
            const { receiptId } = action.payload;
            applyActiveWorkspaceMutation(state, (workspace) => {
                const receipt = (workspace.receipts || []).find((r) => r.id === receiptId);
                if (receipt && receipt.status === 'bozza') {
                    receipt.status = 'emesso';
                    receipt.emittedAt = dayjs().toISOString();
                    if (receipt.receiptType === 'smart_receipt') {
                        receipt.sdiTransmission = { status: 'pending', transmittedAt: null };
                    }
                }
            });

            addNotification(state, {
                id: `notif-receipt-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Corrispettivo emesso',
                description: 'Il corrispettivo è stato registrato correttamente.',
                route: '/finance/corrispettivi',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        cancelActiveReceiptLocally: (state, action) => {
            const { receiptId, reason } = action.payload;
            applyActiveWorkspaceMutation(state, (workspace) => {
                const receipt = (workspace.receipts || []).find((r) => r.id === receiptId);
                if (receipt && receipt.status === 'emesso') {
                    receipt.status = 'annullato';
                    receipt.cancelledAt = dayjs().toISOString();
                    receipt.cancellationReason = reason || '';
                }
            });

            addNotification(state, {
                id: `notif-receipt-cancel-${Date.now()}`,
                level: 'warning',
                unread: true,
                title: 'Corrispettivo annullato',
                description: 'Il corrispettivo è stato annullato.',
                route: '/finance/corrispettivi',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        setBillingChannel: (state, action) => {
            if (state.billingWorkspace) {
                state.billingWorkspace.activeBillingChannel = action.payload;
                persistState(state);
            }
        },
        setActiveB2bInvoice: (state, action) => {
            if (state.billingWorkspace) {
                state.billingWorkspace.activeB2bInvoiceId = action.payload;
                persistState(state);
            }
        },
        addRegistroSezionaleLocally: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                const { channel, companyDocumentId, label, prefix, padDigits, yearSuffix, startingNumber, fiscalYear, notes } = action.payload;
                const registroId = `registro-${channel}-${Date.now()}`;
                workspace.registriSezionali = [
                    {
                        id: registroId,
                        channel: channel || 'b2b',
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        label: label || '',
                        prefix: prefix || 'FT',
                        padDigits: padDigits || 3,
                        yearSuffix: yearSuffix !== false,
                        startingNumber: Number(startingNumber) || 1,
                        currentNumber: Math.max(0, (Number(startingNumber) || 1) - 1),
                        fiscalYear: fiscalYear || dayjs().year(),
                        active: true,
                        notes: notes || '',
                    },
                    ...(workspace.registriSezionali || []),
                ];
            });
        },
        updateRegistroSezionaleLocally: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                const { registroId, changes } = action.payload;
                const index = (workspace.registriSezionali || []).findIndex((r) => r.id === registroId);
                if (index !== -1) {
                    workspace.registriSezionali[index] = { ...workspace.registriSezionali[index], ...changes };
                }
            });
        },
        deleteRegistroSezionaleLocally: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                workspace.registriSezionali = (workspace.registriSezionali || []).filter(
                    (r) => r.id !== action.payload
                );
            });
        },
        toggleRegistroSezionaleActive: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                const registro = (workspace.registriSezionali || []).find((r) => r.id === action.payload);
                if (registro) {
                    registro.active = !registro.active;
                }
            });
        },
        addB2bInvoiceLocally: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                const { companyDocumentId } = action.payload || {};
                const invoiceId = `b2b-inv-${Date.now()}`;
                workspace.b2bInvoices = [
                    {
                        id: invoiceId,
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        referenceNumber: null,
                        issueDate: null,
                        recipient: {
                            businessName: '',
                            vatNumber: '',
                            sdiCode: '',
                            pec: '',
                            address: '',
                        },
                        lines: [
                            {
                                id: `b2b-line-${Date.now()}`,
                                description: '',
                                quantity: 1,
                                unitPrice: 0,
                                vatRate: 22,
                                lineTotal: 0,
                            },
                        ],
                        netAmount: 0,
                        vatAmount: 0,
                        totalAmount: 0,
                        paymentTerms: 'net30',
                        dueDate: null,
                        status: 'bozza',
                        sdiStatus: null,
                        issuedAt: null,
                        notes: '',
                    },
                    ...(workspace.b2bInvoices || []),
                ];
                workspace.activeB2bInvoiceId = invoiceId;
            });
        },
        updateB2bInvoiceLocally: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                const { invoiceId, changes } = action.payload;
                const index = (workspace.b2bInvoices || []).findIndex((inv) => inv.id === invoiceId);
                if (index !== -1) {
                    workspace.b2bInvoices[index] = { ...workspace.b2bInvoices[index], ...changes };
                }
            });
        },
        emitB2bInvoiceLocally: (state, action) => {
            const { invoiceId } = action.payload;
            applyBillingWorkspaceMutation(state, (workspace) => {
                const invoice = (workspace.b2bInvoices || []).find((inv) => inv.id === invoiceId);
                if (invoice && invoice.status === 'bozza') {
                    const activeRegistro = (workspace.registriSezionali || []).find(
                        (r) => r.channel === 'b2b' && r.active && r.companyDocumentId === invoice.companyDocumentId
                    ) || (workspace.registriSezionali || []).find(
                        (r) => r.channel === 'b2b' && r.active
                    );
                    if (activeRegistro && !invoice.referenceNumber) {
                        const nextNumber = (activeRegistro.currentNumber || 0) + 1;
                        invoice.referenceNumber = formatRegistroNumber(activeRegistro, nextNumber);
                        invoice.registroId = activeRegistro.id;
                        activeRegistro.currentNumber = nextNumber;
                    }
                    invoice.status = 'emessa';
                    invoice.sdiStatus = 'in_elaborazione';
                    invoice.issuedAt = dayjs().toISOString();
                    invoice.issueDate = dayjs().format('YYYY-MM-DD');
                }
            });
            addNotification(state, {
                id: `notif-b2b-emit-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Fattura B2B emessa',
                description: 'La fattura è in trasmissione via SDI.',
                route: '/finance/fatturazione-attiva',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        setActiveFreeInvoiceId: (state, action) => {
            if (state.billingWorkspace) {
                state.billingWorkspace.activeFreeInvoiceId = action.payload;
                persistState(state);
            }
        },
        addFreeInvoiceLocally: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                const { companyDocumentId, channel } = action.payload || {};
                const invoiceId = `free-inv-${Date.now()}`;
                workspace.freeInvoices = [
                    {
                        id: invoiceId,
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        channel: channel || 'b2b',
                        referenceNumber: null,
                        issueDate: null,
                        recipient: {
                            name: '',
                            businessName: '',
                            fiscalCode: '',
                            vatNumber: '',
                            address: '',
                            sdiCode: '',
                            pec: '',
                            codiceUnivoco: '',
                        },
                        lines: [
                            {
                                id: `free-line-${Date.now()}`,
                                description: '',
                                quantity: 1,
                                unitPrice: 0,
                                vatRate: 22,
                                lineTotal: 0,
                            },
                        ],
                        netAmount: 0,
                        vatAmount: 0,
                        totalAmount: 0,
                        paymentTerms: 'net30',
                        dueDate: null,
                        status: 'bozza',
                        reconciliationStatus: null,
                        reconciliation: null,
                        registroId: null,
                        issuedAt: null,
                        notes: '',
                    },
                    ...(workspace.freeInvoices || []),
                ];
                workspace.activeFreeInvoiceId = invoiceId;
            });
        },
        updateFreeInvoiceLocally: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                const { invoiceId, changes } = action.payload;
                const index = (workspace.freeInvoices || []).findIndex((inv) => inv.id === invoiceId);
                if (index !== -1) {
                    workspace.freeInvoices[index] = { ...workspace.freeInvoices[index], ...changes };
                }
            });
        },
        emitFreeInvoiceLocally: (state, action) => {
            const { invoiceId } = action.payload;
            applyBillingWorkspaceMutation(state, (workspace) => {
                const invoice = (workspace.freeInvoices || []).find((inv) => inv.id === invoiceId);
                if (invoice && invoice.status === 'bozza') {
                    const activeRegistro = (workspace.registriSezionali || []).find(
                        (r) => r.channel === invoice.channel && r.active && r.companyDocumentId === invoice.companyDocumentId
                    ) || (workspace.registriSezionali || []).find(
                        (r) => r.channel === invoice.channel && r.active
                    );
                    if (activeRegistro && !invoice.referenceNumber) {
                        const nextNumber = (activeRegistro.currentNumber || 0) + 1;
                        invoice.referenceNumber = formatRegistroNumber(activeRegistro, nextNumber);
                        invoice.registroId = activeRegistro.id;
                        activeRegistro.currentNumber = nextNumber;
                    }
                    invoice.status = 'emessa';
                    invoice.reconciliationStatus = 'da_riconciliare';
                    invoice.issuedAt = dayjs().toISOString();
                    invoice.issueDate = dayjs().format('YYYY-MM-DD');
                }
            });
            addNotification(state, {
                id: `notif-free-emit-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Fattura libera emessa',
                description: 'La fattura è stata emessa fuori flusso. Riconciliarla a matricola, carico o pratica per regolarizzare lo scarico.',
                route: '/finance/fattura-libera',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        reconcileFreeInvoiceLocally: (state, action) => {
            const { invoiceId, linkedType, linkedId, linkedLabel, note } = action.payload;
            applyBillingWorkspaceMutation(state, (workspace) => {
                const invoice = (workspace.freeInvoices || []).find((inv) => inv.id === invoiceId);
                if (invoice && invoice.reconciliationStatus === 'da_riconciliare') {
                    invoice.reconciliationStatus = 'riconciliata';
                    invoice.reconciliation = {
                        linkedType,
                        linkedId,
                        linkedLabel: linkedLabel || getFreeInvoiceLinkedTypeLabel(linkedType),
                        reconciledAt: dayjs().toISOString(),
                        note: note || '',
                    };
                }
            });
            addNotification(state, {
                id: `notif-free-reconcile-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Fattura libera riconciliata',
                description: 'La fattura è stata collegata correttamente. Lo scarico è regolarizzato.',
                route: '/finance/fattura-libera',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        setActiveDdtId: (state, action) => {
            if (state.billingWorkspace) {
                state.billingWorkspace.activeDdtId = action.payload;
                persistState(state);
            }
        },
        addActiveDdtLocally: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                const { companyDocumentId, channel, customerName, customerFiscalCode, customerVatNumber } = action.payload || {};
                const ddtId = `act-ddt-${Date.now()}`;
                const ddtNumber = `DDT-${dayjs().format('YYYY')}-${String((workspace.activeDdts || []).length + 1).padStart(3, '0')}`;
                workspace.activeDdts = [
                    {
                        id: ddtId,
                        companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        ddtNumber,
                        issueDate: dayjs().format('YYYY-MM-DD'),
                        channel: channel || 'b2c',
                        customerName: customerName || '',
                        customerFiscalCode: customerFiscalCode || '',
                        customerVatNumber: customerVatNumber || '',
                        lines: [
                            {
                                id: `act-ddt-line-${Date.now()}`,
                                description: '',
                                articleCode: null,
                                serialId: null,
                                quantity: 1,
                                unitPrice: 0,
                                vatRate: 22,
                                lineTotal: 0,
                            },
                        ],
                        netAmount: 0,
                        vatAmount: 0,
                        totalAmount: 0,
                        status: 'bozza',
                        linkedInvoiceId: null,
                        notes: '',
                        issuedAt: null,
                    },
                    ...(workspace.activeDdts || []),
                ];
                workspace.activeDdtId = ddtId;
            });
        },
        updateActiveDdtLocally: (state, action) => {
            applyBillingWorkspaceMutation(state, (workspace) => {
                const { ddtId, changes } = action.payload;
                const index = (workspace.activeDdts || []).findIndex((d) => d.id === ddtId);
                if (index !== -1) {
                    workspace.activeDdts[index] = { ...workspace.activeDdts[index], ...changes };
                }
            });
        },
        emitActiveDdtLocally: (state, action) => {
            const { ddtId } = action.payload;
            applyBillingWorkspaceMutation(state, (workspace) => {
                const ddt = (workspace.activeDdts || []).find((d) => d.id === ddtId);
                if (ddt && ddt.status === 'bozza') {
                    const net = (ddt.lines || []).reduce((sum, l) => sum + Number(l.unitPrice || 0) * Number(l.quantity || 0), 0);
                    const vat = (ddt.lines || []).reduce((sum, l) => sum + Number(l.unitPrice || 0) * Number(l.quantity || 0) * Number(l.vatRate || 22) / 100, 0);
                    ddt.netAmount = Math.round(net * 100) / 100;
                    ddt.vatAmount = Math.round(vat * 100) / 100;
                    ddt.totalAmount = Math.round((net + vat) * 100) / 100;
                    ddt.status = 'emesso';
                    ddt.issuedAt = dayjs().toISOString();
                }
            });
            addNotification(state, {
                id: `notif-act-ddt-emit-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'DDT di vendita emesso',
                description: 'Il documento di trasporto è emesso. Puoi ora creare la fattura differita.',
                route: '/finance/vendita-immediata-differita',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        createInvoiceFromDdtsLocally: (state, action) => {
            const { ddtIds, channel, companyDocumentId } = action.payload;
            applyBillingWorkspaceMutation(state, (workspace) => {
                const ddts = (workspace.activeDdts || []).filter((d) => ddtIds.includes(d.id));
                if (ddts.length === 0) return;

                const activeRegistro = (workspace.registriSezionali || []).find(
                    (r) => r.channel === channel && r.active && r.companyDocumentId === (companyDocumentId || ddts[0].companyDocumentId)
                ) || (workspace.registriSezionali || []).find(
                    (r) => r.channel === channel && r.active
                );

                const firstDdt = ddts[0];
                const allLines = ddts.flatMap((ddt) =>
                    (ddt.lines || []).map((line) => ({
                        ...line,
                        id: `line-${ddt.id}-${line.id}`,
                        description: `${line.description || ''}${ddts.length > 1 ? ` (DDT ${ddt.ddtNumber})` : ''}`.trim(),
                    }))
                );

                const net = allLines.reduce((sum, l) => sum + Number(l.unitPrice || 0) * Number(l.quantity || 0), 0);
                const vat = allLines.reduce((sum, l) => sum + Number(l.unitPrice || 0) * Number(l.quantity || 0) * Number(l.vatRate || 22) / 100, 0);
                const total = Math.round((net + vat) * 100) / 100;

                let referenceNumber = null;
                if (activeRegistro) {
                    const nextNumber = (activeRegistro.currentNumber || 0) + 1;
                    referenceNumber = formatRegistroNumber(activeRegistro, nextNumber);
                    activeRegistro.currentNumber = nextNumber;
                }

                const invoiceId = `free-inv-${Date.now()}`;
                workspace.freeInvoices = [
                    {
                        id: invoiceId,
                        companyDocumentId: companyDocumentId || firstDdt.companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        channel,
                        invoiceType: 'differita',
                        linkedDdtIds: ddtIds,
                        referenceNumber,
                        issueDate: dayjs().format('YYYY-MM-DD'),
                        recipient: {
                            name: firstDdt.customerName || '',
                            businessName: channel === 'b2b' ? (firstDdt.customerName || '') : '',
                            fiscalCode: firstDdt.customerFiscalCode || '',
                            vatNumber: firstDdt.customerVatNumber || '',
                            address: '',
                            sdiCode: '',
                            pec: '',
                            codiceUnivoco: '',
                        },
                        lines: allLines,
                        netAmount: Math.round(net * 100) / 100,
                        vatAmount: Math.round(vat * 100) / 100,
                        totalAmount: total,
                        paymentTerms: 'net30',
                        dueDate: dayjs().add(30, 'day').format('YYYY-MM-DD'),
                        status: 'emessa',
                        reconciliationStatus: 'da_riconciliare',
                        reconciliation: null,
                        registroId: activeRegistro?.id || null,
                        issuedAt: dayjs().toISOString(),
                        notes: `Fattura differita – ${ddts.length > 1 ? `raggruppamento DDT: ${ddts.map((d) => d.ddtNumber).join(', ')}` : `DDT ${firstDdt.ddtNumber}`}`,
                    },
                    ...(workspace.freeInvoices || []),
                ];
                workspace.activeFreeInvoiceId = invoiceId;

                workspace.activeDdts = (workspace.activeDdts || []).map((ddt) =>
                    ddtIds.includes(ddt.id)
                        ? { ...ddt, status: 'fatturato', linkedInvoiceId: invoiceId }
                        : ddt
                );
            });
            addNotification(state, {
                id: `notif-ddt-invoiced-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Fattura differita emessa',
                description: `Fattura differita creata da ${ddtIds.length} DDT. Verifica la riconciliazione in fattura libera.`,
                route: '/finance/vendita-immediata-differita',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        emitImmediateInvoiceLocally: (state, action) => {
            const { customerName, customerFiscalCode, customerVatNumber, channel, companyDocumentId, lines, paymentTerms, createDdt } = action.payload;
            applyBillingWorkspaceMutation(state, (workspace) => {
                const resolvedCompany = companyDocumentId || state.activeCompanyDocumentId || 'aurifon';
                const resolvedChannel = channel || 'b2c';
                const resolvedLines = (lines || []).map((line, i) => ({
                    id: `imm-line-${Date.now()}-${i}`,
                    description: line.description || '',
                    articleCode: line.articleCode || null,
                    serialId: line.serialId || null,
                    quantity: Number(line.quantity || 1),
                    unitPrice: Number(line.unitPrice || 0),
                    vatRate: Number(line.vatRate || 22),
                    lineTotal: Math.round(Number(line.unitPrice || 0) * Number(line.quantity || 1) * 100) / 100,
                }));

                const net = resolvedLines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
                const vat = resolvedLines.reduce((sum, l) => sum + l.unitPrice * l.quantity * l.vatRate / 100, 0);
                const total = Math.round((net + vat) * 100) / 100;

                const activeRegistro = (workspace.registriSezionali || []).find(
                    (r) => r.channel === resolvedChannel && r.active && r.companyDocumentId === resolvedCompany
                ) || (workspace.registriSezionali || []).find(
                    (r) => r.channel === resolvedChannel && r.active
                );

                let referenceNumber = null;
                if (activeRegistro) {
                    const nextNumber = (activeRegistro.currentNumber || 0) + 1;
                    referenceNumber = formatRegistroNumber(activeRegistro, nextNumber);
                    activeRegistro.currentNumber = nextNumber;
                }

                let ddtId = null;
                if (createDdt) {
                    ddtId = `act-ddt-${Date.now()}`;
                    const ddtNumber = `DDT-${dayjs().format('YYYY')}-${String((workspace.activeDdts || []).length + 1).padStart(3, '0')}`;
                    workspace.activeDdts = [
                        {
                            id: ddtId,
                            companyDocumentId: resolvedCompany,
                            ddtNumber,
                            issueDate: dayjs().format('YYYY-MM-DD'),
                            channel: resolvedChannel,
                            customerName: customerName || '',
                            customerFiscalCode: customerFiscalCode || '',
                            customerVatNumber: customerVatNumber || '',
                            lines: resolvedLines,
                            netAmount: Math.round(net * 100) / 100,
                            vatAmount: Math.round(vat * 100) / 100,
                            totalAmount: total,
                            status: 'fatturato',
                            linkedInvoiceId: null,
                            notes: 'DDT emesso contestualmente alla fattura immediata.',
                            issuedAt: dayjs().toISOString(),
                        },
                        ...(workspace.activeDdts || []),
                    ];
                }

                const invoiceId = `free-inv-${Date.now()}`;
                workspace.freeInvoices = [
                    {
                        id: invoiceId,
                        companyDocumentId: resolvedCompany,
                        channel: resolvedChannel,
                        invoiceType: 'immediata',
                        linkedDdtIds: ddtId ? [ddtId] : [],
                        referenceNumber,
                        issueDate: dayjs().format('YYYY-MM-DD'),
                        recipient: {
                            name: resolvedChannel === 'b2c' ? (customerName || '') : '',
                            businessName: resolvedChannel !== 'b2c' ? (customerName || '') : '',
                            fiscalCode: customerFiscalCode || '',
                            vatNumber: customerVatNumber || '',
                            address: '',
                            sdiCode: '',
                            pec: '',
                            codiceUnivoco: '',
                        },
                        lines: resolvedLines,
                        netAmount: Math.round(net * 100) / 100,
                        vatAmount: Math.round(vat * 100) / 100,
                        totalAmount: total,
                        paymentTerms: paymentTerms || 'immediato',
                        dueDate: dayjs().format('YYYY-MM-DD'),
                        status: 'emessa',
                        reconciliationStatus: 'da_riconciliare',
                        reconciliation: null,
                        registroId: activeRegistro?.id || null,
                        issuedAt: dayjs().toISOString(),
                        notes: createDdt ? `Fattura immediata – con DDT allegato` : 'Fattura immediata senza DDT.',
                    },
                    ...(workspace.freeInvoices || []),
                ];
                workspace.activeFreeInvoiceId = invoiceId;

                if (ddtId) {
                    workspace.activeDdts = (workspace.activeDdts || []).map((ddt) =>
                        ddt.id === ddtId ? { ...ddt, linkedInvoiceId: invoiceId } : ddt
                    );
                }
            });
            addNotification(state, {
                id: `notif-immediate-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Vendita immediata registrata',
                description: `Fattura immediata emessa${action.payload.createDdt ? ' con DDT allegato' : ''}.`,
                route: '/finance/vendita-immediata-differita',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        convertTrialToAgreementLocally: (state, action) => {
            const { serialId } = action.payload;
            const serial = (state.inventoryWorkspace?.serials || []).find((s) => s.id === serialId);
            if (!serial) return;
            const article = (state.inventoryWorkspace?.articles || []).find((a) => a.code === serial.articleCode);
            const agreementId = `agreement-trial-${Date.now()}`;
            applyActiveWorkspaceMutation(state, (workspace) => {
                workspace.agreements = [
                    {
                        id: agreementId,
                        companyDocumentId: serial.companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                        patientName: serial.patientName || '',
                        patientCode: '',
                        serialId: serial.id,
                        serialNumber: serial.serialNumber,
                        articleCode: serial.articleCode || null,
                        articleLabel: article?.label || serial.articleCode || '',
                        dealType: 'privato',
                        price: 0,
                        discount: 0,
                        aslQuota: 0,
                        privateQuota: 0,
                        paymentMethod: 'contanti',
                        installments: [],
                        status: 'bozza',
                        linkedDocumentId: null,
                        createdAt: dayjs().toISOString(),
                        confirmedAt: null,
                        notes: `Accordo generato da prova avviata il ${dayjs(serial.trialStartedAt).format('DD/MM/YYYY')} (${dayjs().diff(dayjs(serial.trialStartedAt), 'day')} giorni).`,
                    },
                    ...(workspace.agreements || []),
                ];
                workspace.activeAgreementId = agreementId;
            });
            addNotification(state, {
                id: `notif-trial-convert-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Accordo creato da prova',
                description: `Accordo in bozza creato per ${serial.patientName || 'paziente'} – matricola ${serial.serialNumber}.`,
                route: '/finance/attivo',
                companyDocumentId: state.activeCompanyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        setActiveNsoOrderId: (state, action) => {
            state.complianceWorkspace.activeNsoOrderId = action.payload;
        },
        addNsoOrderLocally: (state, action) => {
            const { order } = action.payload;
            state.complianceWorkspace.nsoOrders = [order, ...(state.complianceWorkspace.nsoOrders || [])];
            state.complianceWorkspace.activeNsoOrderId = order.id;
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            addNotification(state, {
                id: `notif-nso-import-${Date.now()}`,
                level: 'info',
                unread: true,
                title: `Ordine ${order.nsoNumber} importato`,
                description: `Ordine da ${order.aslName} per ${order.patientName} aggiunto all'inbox.`,
                route: '/finance/ordini-nso-mso',
                companyDocumentId: order.companyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        setActivePraticaId: (state, action) => {
            state.complianceWorkspace.activePraticaId = action.payload;
        },
        setActiveFatturaPAId: (state, action) => {
            state.complianceWorkspace.activeFatturaPAId = action.payload;
        },
        setActiveStsExportId: (state, action) => {
            state.complianceWorkspace.activeStsExportId = action.payload;
        },
        receiveNsoOrderLocally: (state, action) => {
            const { nsoOrderId } = action.payload;
            const order = (state.complianceWorkspace?.nsoOrders || []).find((o) => o.id === nsoOrderId);
            if (!order) return;
            order.status = 'preso_in_carico';
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            addNotification(state, {
                id: `notif-nso-receive-${Date.now()}`,
                level: 'info',
                unread: true,
                title: `Ordine NSO ${order.nsoNumber} preso in carico`,
                description: `Ordine da ${order.aslName} per ${order.patientName} preso in carico.`,
                route: '/finance/compliance',
                companyDocumentId: order.companyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        evadeNsoOrderLocally: (state, action) => {
            const { nsoOrderId, linkedPraticaId } = action.payload;
            const order = (state.complianceWorkspace?.nsoOrders || []).find((o) => o.id === nsoOrderId);
            if (!order) return;
            order.status = 'evaso';
            order.processedAt = dayjs().format('YYYY-MM-DD');
            if (linkedPraticaId) {
                order.linkedPraticaId = linkedPraticaId;
            }
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        addPraticaAslLocally: (state, action) => {
            const { companyDocumentId, patientName, patientCf, aslName, aslCodiceUnivoco, aslQuota, privateQuota, splitPayment, quotaResiduaCarico, riconducibileLabel, linkedNsoOrderId } = action.payload;
            const praticaId = `pratica-asl-${Date.now()}`;
            const newPratica = {
                id: praticaId,
                companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                patientName: patientName || '',
                patientCf: patientCf || '',
                aslName: aslName || '',
                aslCodiceUnivoco: aslCodiceUnivoco || '',
                linkedNsoOrderId: linkedNsoOrderId || null,
                linkedAgreementId: null,
                aslQuota: aslQuota || 0,
                privateQuota: privateQuota || 0,
                splitPayment: splitPayment !== undefined ? splitPayment : true,
                quotaResiduaCarico: quotaResiduaCarico || 'cittadino',
                riconducibileLabel: riconducibileLabel || '',
                status: linkedNsoOrderId ? 'ordine_ricevuto' : 'aperta',
                linkedFatturaPAId: null,
                openedAt: dayjs().format('YYYY-MM-DD'),
                ordineRicevutoAt: linkedNsoOrderId ? dayjs().format('YYYY-MM-DD') : null,
                provaAt: null,
                consegnaAt: null,
                fatturatoAt: null,
                pagatoAt: null,
                closedAt: null,
                notes: '',
            };
            state.complianceWorkspace.pratiche = [newPratica, ...(state.complianceWorkspace.pratiche || [])];
            state.complianceWorkspace.activePraticaId = praticaId;
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        updatePraticaAslLocally: (state, action) => {
            const { praticaId, changes } = action.payload;
            const pratica = (state.complianceWorkspace?.pratiche || []).find((p) => p.id === praticaId);
            if (!pratica) return;
            Object.assign(pratica, changes);
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        advancePraticaAslToProva: (state, action) => {
            const { praticaId } = action.payload;
            const pratica = (state.complianceWorkspace?.pratiche || []).find((p) => p.id === praticaId);
            if (!pratica) return;
            pratica.status = 'in_prova';
            pratica.provaAt = dayjs().format('YYYY-MM-DD');
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        advancePraticaAslToConsegnato: (state, action) => {
            const { praticaId } = action.payload;
            const pratica = (state.complianceWorkspace?.pratiche || []).find((p) => p.id === praticaId);
            if (!pratica) return;
            pratica.status = 'consegnato';
            pratica.consegnaAt = dayjs().format('YYYY-MM-DD');
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        advancePraticaAslToFatturato: (state, action) => {
            const { praticaId } = action.payload;
            const pratica = (state.complianceWorkspace?.pratiche || []).find((p) => p.id === praticaId);
            if (!pratica) return;
            pratica.status = 'fatturato';
            pratica.fatturatoAt = dayjs().format('YYYY-MM-DD');
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        advancePraticaAslToPagato: (state, action) => {
            const { praticaId } = action.payload;
            const pratica = (state.complianceWorkspace?.pratiche || []).find((p) => p.id === praticaId);
            if (!pratica) return;
            pratica.status = 'pagato';
            pratica.pagatoAt = dayjs().format('YYYY-MM-DD');
            pratica.closedAt = dayjs().format('YYYY-MM-DD');
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        addFatturaPALocally: (state, action) => {
            const {
                praticaId,
                companyDocumentId,
                patientName: patientNameOverride,
                aslName: aslNameOverride,
                aslCodiceUnivoco: aslCodiceUnivocoOverride,
                amount: amountOverride,
                vatRate: vatRateOverride,
                notes: notesOverride,
                cig,
                cup,
                rifOrdineNumero,
                rifOrdineData,
                rifOrdineCodice,
                nsoNumero,
                nsoData,
                nsoTipoDocumento,
                splitPayment,
                quotaResiduaCarico,
                riconducibileLabel,
            } = action.payload;
            const pratica = (state.complianceWorkspace?.pratiche || []).find((p) => p.id === praticaId);
            const fatturaPAId = `fattura-pa-${Date.now()}`;
            const referenceNumber = `FPA-${Date.now().toString().slice(-6)}`;
            const amount = amountOverride !== undefined ? amountOverride : (pratica?.aslQuota || 0);
            const vatRate = vatRateOverride !== undefined ? vatRateOverride : 4;
            const vatAmount = Math.round(amount * (vatRate / 100) * 100) / 100;
            const total = Math.round((amount + vatAmount) * 100) / 100;
            const newFattura = {
                id: fatturaPAId,
                companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                praticaId: praticaId || null,
                patientName: patientNameOverride !== undefined ? patientNameOverride : (pratica?.patientName || ''),
                aslName: aslNameOverride !== undefined ? aslNameOverride : (pratica?.aslName || ''),
                aslCodiceUnivoco: aslCodiceUnivocoOverride !== undefined ? aslCodiceUnivocoOverride : (pratica?.aslCodiceUnivoco || ''),
                referenceNumber,
                amount,
                vatRate,
                vatAmount,
                total,
                cig: cig || '',
                cup: cup || '',
                rifOrdineNumero: rifOrdineNumero || '',
                rifOrdineData: rifOrdineData || '',
                rifOrdineCodice: rifOrdineCodice || '',
                nsoNumero: nsoNumero || '',
                nsoData: nsoData || '',
                nsoTipoDocumento: nsoTipoDocumento || '',
                splitPayment: splitPayment !== undefined ? splitPayment : (pratica?.splitPayment !== undefined ? pratica.splitPayment : true),
                quotaResiduaCarico: quotaResiduaCarico || pratica?.quotaResiduaCarico || 'cittadino',
                riconducibileLabel: riconducibileLabel !== undefined ? riconducibileLabel : (pratica?.riconducibileLabel || ''),
                privateQuota: pratica?.privateQuota || 0,
                status: 'bozza',
                emittedAt: null,
                xmlPreview: null,
                notes: notesOverride !== undefined ? notesOverride : '',
                createdAt: dayjs().toISOString(),
            };
            state.complianceWorkspace.fatturePA = [newFattura, ...(state.complianceWorkspace.fatturePA || [])];
            state.complianceWorkspace.activeFatturaPAId = fatturaPAId;
            if (pratica) {
                pratica.status = 'in_fatturazione';
                pratica.linkedFatturaPAId = fatturaPAId;
            }
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        emitFatturaPALocally: (state, action) => {
            const { fatturaPAId } = action.payload;
            const fattura = (state.complianceWorkspace?.fatturePA || []).find((f) => f.id === fatturaPAId);
            if (!fattura) return;
            const activeRegistro = (state.billingWorkspace?.registriSezionali || []).find(
                (r) => r.channel === 'b2g' && r.active && r.companyDocumentId === fattura.companyDocumentId
            ) || (state.billingWorkspace?.registriSezionali || []).find(
                (r) => r.channel === 'b2g' && r.active
            );
            if (activeRegistro) {
                const nextNumber = (activeRegistro.currentNumber || 0) + 1;
                fattura.referenceNumber = formatRegistroNumber(activeRegistro, nextNumber);
                fattura.registroId = activeRegistro.id;
                applyBillingWorkspaceMutation(state, (workspace) => {
                    const reg = (workspace.registriSezionali || []).find((r) => r.id === activeRegistro.id);
                    if (reg) reg.currentNumber = nextNumber;
                });
            }
            fattura.status = 'inviata_sdi';
            fattura.emittedAt = dayjs().format('YYYY-MM-DD');
            const pratica = (state.complianceWorkspace?.pratiche || []).find((p) => p.id === fattura.praticaId);
            if (pratica) {
                pratica.status = 'fatturata';
                pratica.closedAt = dayjs().format('YYYY-MM-DD');
            }
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            addNotification(state, {
                id: `notif-fpa-emit-${Date.now()}`,
                level: 'success',
                unread: true,
                title: `Fattura PA ${fattura.referenceNumber} inviata SDI`,
                description: `Fattura PA per ${fattura.patientName} inviata al SDI.`,
                route: '/finance/compliance',
                companyDocumentId: fattura.companyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        addStsExportLocally: (state, action) => {
            const { companyDocumentId, period } = action.payload;
            const stsExportId = `sts-${Date.now()}`;
            const newExport = {
                id: stsExportId,
                companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                period: period || '',
                entries: [],
                totalEntries: 0,
                totalAmount: 0,
                status: 'bozza',
                exportedAt: null,
            };
            state.complianceWorkspace.stsExports = [newExport, ...(state.complianceWorkspace.stsExports || [])];
            state.complianceWorkspace.activeStsExportId = stsExportId;
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        prepareStsExportLocally: (state, action) => {
            const { stsExportId } = action.payload;
            const stsExport = (state.complianceWorkspace?.stsExports || []).find((s) => s.id === stsExportId);
            if (!stsExport) return;
            stsExport.status = 'pronta_export';
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        submitStsExportLocally: (state, action) => {
            const { stsExportId } = action.payload;
            const stsExport = (state.complianceWorkspace?.stsExports || []).find((s) => s.id === stsExportId);
            if (!stsExport) return;
            stsExport.status = 'inviata';
            stsExport.exportedAt = dayjs().toISOString();
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            addNotification(state, {
                id: `notif-sts-submit-${Date.now()}`,
                level: 'success',
                unread: true,
                title: `Export STS ${stsExport.period} inviato`,
                description: `Flusso STS per ${stsExport.period} inviato al Sistema Tessera Sanitaria.`,
                route: '/finance/compliance',
                companyDocumentId: stsExport.companyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        downloadStsLightLocally: (state, action) => {
            const { stsExportId } = action.payload;
            const stsExport = (state.complianceWorkspace?.stsExports || []).find((s) => s.id === stsExportId);
            if (!stsExport) return;
            stsExport.lightDownloadedAt = dayjs().toISOString();
            const lightExportId = `acc-export-light-${Date.now()}`;
            const lightExport = {
                id: lightExportId,
                companyDocumentId: stsExport.companyDocumentId,
                type: 'sts_light',
                period: stsExport.period,
                status: 'pronto',
                generatedAt: dayjs().toISOString(),
                entries: stsExport.entries || [],
            };
            state.complianceWorkspace.accountantExports = [
                lightExport,
                ...(state.complianceWorkspace.accountantExports || []),
            ];
            addNotification(state, {
                id: `notif-sts-light-${Date.now()}`,
                level: 'info',
                unread: true,
                title: `Report STS light ${stsExport.period} generato`,
                description: `Report STS light per ${stsExport.period} pronto per il commercialista. Nessun invio diretto a STS effettuato.`,
                route: '/finance/compliance',
                companyDocumentId: stsExport.companyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        addAccountantExportLocally: (state, action) => {
            const { type, period, companyDocumentId } = action.payload;
            const exportId = `acc-export-${Date.now()}`;
            const newExport = {
                id: exportId,
                companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                type,
                period,
                status: 'pronto',
                generatedAt: dayjs().toISOString(),
                entries: [],
            };
            state.complianceWorkspace.accountantExports = [newExport, ...(state.complianceWorkspace.accountantExports || [])];
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        markAccountantExportSentLocally: (state, action) => {
            const { exportId } = action.payload;
            const exp = (state.complianceWorkspace?.accountantExports || []).find((e) => e.id === exportId);
            if (!exp) return;
            exp.status = 'inviato';
            state.complianceWorkspace = recalculateComplianceWorkspace(state.complianceWorkspace);
            persistState(state);
        },
        setActiveMigrationId: (state, action) => {
            if (!state.migrationWorkspace) {
                state.migrationWorkspace = { activeMigrationId: null, migrations: [] };
            }
            state.migrationWorkspace.activeMigrationId = action.payload;
        },
        initPraticaMigrationLocally: (state, action) => {
            const { praticaId, praticaType, patientName, patientCf, amount, linkedDocumentIds, note } = action.payload;
            const migrationId = `migration-${Date.now()}`;
            const now = dayjs().toISOString();
            const migration = {
                id: migrationId,
                sourceCompanyDocumentId: 'medical-audio',
                targetCompanyDocumentId: 'aurifon',
                praticaType: praticaType || 'agreement',
                praticaId,
                patientName: patientName || null,
                patientCf: patientCf || null,
                amount: amount || 0,
                status: 'in_corso',
                linkedDocumentIds: linkedDocumentIds || [],
                auditLog: [
                    { at: now, event: 'avviata', note: note || 'Migrazione avviata.' },
                ],
                initiatedAt: now,
                completedAt: null,
                cancelledAt: null,
                note: note || '',
            };
            if (!state.migrationWorkspace) {
                state.migrationWorkspace = { activeMigrationId: null, migrations: [] };
            }
            state.migrationWorkspace.migrations = [migration, ...(state.migrationWorkspace.migrations || [])];
            state.migrationWorkspace.activeMigrationId = migrationId;
            if (praticaType === 'agreement' || !praticaType) {
                const agreement = (state.activeWorkspace?.agreements || []).find((a) => a.id === praticaId);
                if (agreement) {
                    agreement.status = 'in_migrazione';
                }
            }
            addNotification(state, {
                id: `notif-migration-init-${Date.now()}`,
                level: 'info',
                unread: true,
                title: 'Migrazione pratica avviata',
                description: `${patientName || 'Paziente'} – pratica in migrazione da Medical Audio ad Aurifon.`,
                route: '/finance/migrazione-pratica',
                companyDocumentId: 'medical-audio',
                createdAt: now,
            });
            persistState(state);
        },
        completePraticaMigrationLocally: (state, action) => {
            const { migrationId, note } = action.payload;
            if (!state.migrationWorkspace) return;
            const migration = (state.migrationWorkspace.migrations || []).find((m) => m.id === migrationId);
            if (!migration || migration.status !== 'in_corso') return;
            const now = dayjs().toISOString();
            migration.status = 'completata';
            migration.completedAt = now;
            migration.auditLog = [
                ...migration.auditLog,
                { at: now, event: 'completata', note: note || 'Migrazione completata. Anagrafica e documenti riassegnati ad Aurifon.' },
            ];
            if (migration.praticaType === 'agreement' || !migration.praticaType) {
                const agreement = (state.activeWorkspace?.agreements || []).find((a) => a.id === migration.praticaId);
                if (agreement) {
                    agreement.companyDocumentId = migration.targetCompanyDocumentId;
                    if (agreement.status === 'in_migrazione') {
                        agreement.status = 'confermato';
                    }
                }
            }
            const linkedDocIds = migration.linkedDocumentIds || [];
            if (linkedDocIds.length > 0) {
                state.documents = (state.documents || []).map((doc) =>
                    linkedDocIds.includes(doc.id)
                        ? { ...doc, companyDocumentId: migration.targetCompanyDocumentId }
                        : doc
                );
            }
            addNotification(state, {
                id: `notif-migration-done-${Date.now()}`,
                level: 'success',
                unread: true,
                title: 'Migrazione pratica completata',
                description: `${migration.patientName || 'Paziente'} – pratica trasferita ad Aurifon con anagrafica e riferimenti aggiornati.`,
                route: '/finance/migrazione-pratica',
                companyDocumentId: migration.targetCompanyDocumentId,
                createdAt: now,
            });
            persistState(state);
        },
        cancelPraticaMigrationLocally: (state, action) => {
            const { migrationId, note } = action.payload;
            if (!state.migrationWorkspace) return;
            const migration = (state.migrationWorkspace.migrations || []).find((m) => m.id === migrationId);
            if (!migration || migration.status !== 'in_corso') return;
            const now = dayjs().toISOString();
            migration.status = 'annullata';
            migration.cancelledAt = now;
            migration.auditLog = [
                ...migration.auditLog,
                { at: now, event: 'annullata', note: note || 'Migrazione annullata.' },
            ];
            if (migration.praticaType === 'agreement' || !migration.praticaType) {
                const agreement = (state.activeWorkspace?.agreements || []).find((a) => a.id === migration.praticaId);
                if (agreement && agreement.status === 'in_migrazione') {
                    agreement.status = 'confermato';
                }
            }
            addNotification(state, {
                id: `notif-migration-cancel-${Date.now()}`,
                level: 'warning',
                unread: true,
                title: 'Migrazione pratica annullata',
                description: `${migration.patientName || 'Paziente'} – migrazione annullata. Pratica rimane in Medical Audio.`,
                route: '/finance/migrazione-pratica',
                companyDocumentId: migration.sourceCompanyDocumentId,
                createdAt: now,
            });
            persistState(state);
        },
        setActiveTemplateId: (state, action) => {
            if (!state.templateEditorWorkspace) {
                state.templateEditorWorkspace = { activeTemplateId: null, templates: [], summary: null };
            }
            state.templateEditorWorkspace.activeTemplateId = action.payload;
            persistState(state);
        },
        addTemplateLocally: (state, action) => {
            if (!state.templateEditorWorkspace) {
                state.templateEditorWorkspace = { activeTemplateId: null, templates: [], summary: null };
            }
            const { type, name, companyDocumentId } = action.payload || {};
            const templateId = `template-${Date.now()}`;
            const preset = TEMPLATE_FIELD_PRESETS[type] || [];
            const template = {
                id: templateId,
                name: name || 'Nuovo modello',
                type: type || 'mds_ddt',
                description: '',
                status: 'bozza',
                companyDocumentId: companyDocumentId || state.activeCompanyDocumentId,
                fieldMappings: preset.map((f) => ({ ...f })),
                createdAt: dayjs().toISOString(),
                updatedAt: dayjs().toISOString(),
            };
            state.templateEditorWorkspace.templates = [template, ...(state.templateEditorWorkspace.templates || [])];
            state.templateEditorWorkspace.activeTemplateId = templateId;
            state.templateEditorWorkspace = recalculateTemplateEditorWorkspace(state.templateEditorWorkspace);
            persistState(state);
        },
        updateTemplateLocally: (state, action) => {
            if (!state.templateEditorWorkspace) return;
            const { templateId, changes } = action.payload;
            const templates = state.templateEditorWorkspace.templates || [];
            const idx = templates.findIndex((t) => t.id === templateId);
            if (idx !== -1) {
                templates[idx] = { ...templates[idx], ...changes, updatedAt: dayjs().toISOString() };
                state.templateEditorWorkspace = recalculateTemplateEditorWorkspace(state.templateEditorWorkspace);
                persistState(state);
            }
        },
        updateTemplateFieldMappingLocally: (state, action) => {
            if (!state.templateEditorWorkspace) return;
            const { templateId, fieldId, changes } = action.payload;
            const templates = state.templateEditorWorkspace.templates || [];
            const idx = templates.findIndex((t) => t.id === templateId);
            if (idx !== -1) {
                const template = templates[idx];
                const fieldIdx = (template.fieldMappings || []).findIndex((f) => f.id === fieldId);
                if (fieldIdx !== -1) {
                    template.fieldMappings[fieldIdx] = { ...template.fieldMappings[fieldIdx], ...changes };
                    template.updatedAt = dayjs().toISOString();
                    state.templateEditorWorkspace = recalculateTemplateEditorWorkspace(state.templateEditorWorkspace);
                    persistState(state);
                }
            }
        },
        activateTemplateLocally: (state, action) => {
            if (!state.templateEditorWorkspace) return;
            const { templateId } = action.payload;
            const templates = state.templateEditorWorkspace.templates || [];
            const idx = templates.findIndex((t) => t.id === templateId);
            if (idx !== -1 && templates[idx].status === 'bozza') {
                templates[idx].status = 'attivo';
                templates[idx].updatedAt = dayjs().toISOString();
                state.templateEditorWorkspace = recalculateTemplateEditorWorkspace(state.templateEditorWorkspace);
                addNotification(state, {
                    id: `notif-template-activate-${Date.now()}`,
                    level: 'success',
                    unread: true,
                    title: 'Modello attivato',
                    description: `Il modello "${templates[idx].name}" è ora attivo.`,
                    route: '/finance/template-editor-mds-asl',
                    companyDocumentId: state.activeCompanyDocumentId,
                    createdAt: dayjs().toISOString(),
                });
                persistState(state);
            }
        },
        archiveTemplateLocally: (state, action) => {
            if (!state.templateEditorWorkspace) return;
            const { templateId } = action.payload;
            const templates = state.templateEditorWorkspace.templates || [];
            const idx = templates.findIndex((t) => t.id === templateId);
            if (idx !== -1) {
                templates[idx].status = 'archiviato';
                templates[idx].updatedAt = dayjs().toISOString();
                state.templateEditorWorkspace = recalculateTemplateEditorWorkspace(state.templateEditorWorkspace);
                persistState(state);
            }
        },
        setActivePackageId: (state, action) => {
            if (!state.accountantPackageWorkspace) {
                state.accountantPackageWorkspace = { activePackageId: null, packages: [] };
            }
            state.accountantPackageWorkspace.activePackageId = action.payload;
        },
        generateAccountantPackageLocally: (state, action) => {
            const { quarter, year, companyDocumentId } = action.payload;
            if (!state.accountantPackageWorkspace) {
                state.accountantPackageWorkspace = { activePackageId: null, packages: [] };
            }
            const pkg = buildAccountantPackage({
                quarter,
                year,
                companyDocumentId: companyDocumentId || state.activeCompanyDocumentId || 'aurifon',
                passiveWorkspace: state.passiveWorkspace,
                billingWorkspace: state.billingWorkspace,
                activeWorkspace: state.activeWorkspace,
                complianceWorkspace: state.complianceWorkspace,
            });
            state.accountantPackageWorkspace.packages = [pkg, ...(state.accountantPackageWorkspace.packages || [])];
            state.accountantPackageWorkspace.activePackageId = pkg.id;
            addNotification(state, {
                id: `notif-pkg-generated-${Date.now()}`,
                level: 'success',
                unread: true,
                title: `Pacchetto ${pkg.period} generato`,
                description: `Registri IVA acquisti (${pkg.registroAcquisti.length} doc.) e vendite (${pkg.registroVendite.length} doc.) pronti per il commercialista.`,
                route: '/finance/pacchetto-trimestrale-commercialista',
                companyDocumentId: pkg.companyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        markAccountantPackageSentLocally: (state, action) => {
            const { packageId } = action.payload;
            if (!state.accountantPackageWorkspace) return;
            const pkg = (state.accountantPackageWorkspace.packages || []).find((p) => p.id === packageId);
            if (!pkg) return;
            pkg.status = 'inviato';
            pkg.sentAt = dayjs().toISOString();
            addNotification(state, {
                id: `notif-pkg-sent-${Date.now()}`,
                level: 'info',
                unread: true,
                title: `Pacchetto ${pkg.period} inviato`,
                description: 'Pacchetto trimestrale segnato come inviato al commercialista.',
                route: '/finance/pacchetto-trimestrale-commercialista',
                companyDocumentId: pkg.companyDocumentId,
                createdAt: dayjs().toISOString(),
            });
            persistState(state);
        },
        deleteAccountantPackageLocally: (state, action) => {
            const { packageId } = action.payload;
            if (!state.accountantPackageWorkspace) return;
            state.accountantPackageWorkspace.packages = (state.accountantPackageWorkspace.packages || []).filter(
                (p) => p.id !== packageId
            );
            if (state.accountantPackageWorkspace.activePackageId === packageId) {
                state.accountantPackageWorkspace.activePackageId =
                    (state.accountantPackageWorkspace.packages[0]?.id) || null;
            }
            persistState(state);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(bootstrapRoadmapWorkspace.pending, (state) => {
                state.loadingWorkspace = true;
                state.errorWorkspace = false;
            })
            .addCase(bootstrapRoadmapWorkspace.fulfilled, (state, action) => {
                state.loadingWorkspace = false;
                state.loadedWorkspace = true;
                state.errorWorkspace = false;
                state.activeCompanyDocumentId = action.payload.activeCompanyDocumentId;
                state.businessRole = action.payload.businessRole;
                state.companies = action.payload.companies;
                state.documents = action.payload.documents;
                state.archiveDocuments = action.payload.archiveDocuments || [];
                state.selectedFiscalYears = action.payload.selectedFiscalYears || [dayjs().year()];
                state.closedFiscalYears = action.payload.closedFiscalYears || Array.from({ length: 10 }, (_, i) => dayjs().year() - 10 + i);
                state.passiveWorkspace = action.payload.passiveWorkspace;
                state.treasuryWorkspace = action.payload.treasuryWorkspace;
                state.inventoryWorkspace = action.payload.inventoryWorkspace;
                state.foreignInvoiceWorkspace = action.payload.foreignInvoiceWorkspace;
                state.activeWorkspace = action.payload.activeWorkspace;
                if (action.payload.complianceWorkspace) {
                    state.complianceWorkspace = action.payload.complianceWorkspace;
                }
                if (action.payload.billingWorkspace) {
                    state.billingWorkspace = action.payload.billingWorkspace;
                }
                if (action.payload.migrationWorkspace) {
                    state.migrationWorkspace = action.payload.migrationWorkspace;
                }
                if (action.payload.templateEditorWorkspace) {
                    state.templateEditorWorkspace = action.payload.templateEditorWorkspace;
                }
                if (action.payload.accountantPackageWorkspace) {
                    state.accountantPackageWorkspace = action.payload.accountantPackageWorkspace;
                }
                state.notifications = action.payload.notifications;
                state.playbooks = action.payload.playbooks;
                state.helpArticles = action.payload.helpArticles;
                state.pendingSaves = action.payload.pendingSaves;
                state.billingHubPassive = action.payload.billingHubPassive || state.billingHubPassive;
                state.networkMode = action.payload.networkMode;
                state.lastSyncedAt = action.payload.lastSyncedAt;
                state.loadedAt = action.payload.loadedAt;
                persistState(state);
            })
            .addCase(bootstrapRoadmapWorkspace.rejected, (state) => {
                state.loadingWorkspace = false;
                state.loadedWorkspace = true;
                state.errorWorkspace = true;
            })
            .addCase(syncPassiveInvoicesFromBillingHub.pending, (state, action) => {
                state.billingHubPassive = {
                    ...(state.billingHubPassive || {}),
                    loading: true,
                    error: null,
                    companyDocumentId: action.meta?.arg?.companyDocumentId || state.billingHubPassive?.companyDocumentId || null,
                };
            })
            .addCase(syncPassiveInvoicesFromBillingHub.fulfilled, (state, action) => {
                const incomingDocuments = action.payload?.documents || [];
                const incomingInvoices = action.payload?.invoices || {};
                const existingInvoices = state.passiveWorkspace?.invoices || {};
                const incomingServiceInvoiceIds = new Set(
                    incomingDocuments
                        .filter((document) => document.type === 'service_invoice')
                        .map((document) => document.id)
                );

                state.documents = [
                    ...state.documents.filter((document) => !isPassiveInvoiceDocument(document)),
                    ...incomingDocuments.map((document) => {
                        const existingDocument = state.documents.find((item) => item.id === document.id);
                        return {
                            ...document,
                            status: existingDocument?.status === 'contabilizzata' ? existingDocument.status : document.status,
                            notes: existingDocument?.notes || document.notes,
                        };
                    }),
                ];

                const mergedInvoices = {};
                Object.entries(incomingInvoices).forEach(([invoiceId, invoice]) => {
                    const existing = existingInvoices[invoiceId] || {};
                    const existingDirectLines = (existing.directLines || []).filter(
                        (line) => !String(line?.id || '').startsWith(`billing-line-${invoiceId}-`)
                    );
                    const directLines = existingDirectLines.length > 0
                        ? existingDirectLines
                        : invoice.directLines;
                    const selectedDdtIds = existing.selectedDdtIds || invoice.selectedDdtIds;
                    mergedInvoices[invoiceId] = {
                        ...invoice,
                        ...existing,
                        acubeEvent: invoice.acubeEvent,
                        billingHubStatus: invoice.billingHubStatus,
                        documentType: invoice.documentType,
                        xmlSupplierName: invoice.xmlSupplierName,
                        xmlSupplierVat: invoice.xmlSupplierVat,
                        xmlTotals: invoice.xmlTotals,
                        issueDate: invoice.issueDate,
                        supplierReferenceNumber: invoice.supplierReferenceNumber,
                        supplierKnown: existing.supplierUnlinkedManually
                            ? false
                            : existing.supplierKnown || invoice.supplierKnown,
                        supplierUnlinkedManually: Boolean(existing.supplierUnlinkedManually),
                        selectedDdtIds,
                        lineOverrides: existing.lineOverrides || invoice.lineOverrides,
                        extraLines: existing.extraLines || invoice.extraLines,
                        directLines,
                        serviceFlow: incomingServiceInvoiceIds.has(invoiceId)
                            ? Boolean(existing.serviceFlow || invoice.serviceFlow)
                            : false,
                        reviewStatus: existing.reviewStatus === 'contabilizzata' ? existing.reviewStatus : invoice.reviewStatus,
                    };
                });

                const currentSelectedInvoiceId = state.passiveWorkspace?.selectedInvoiceId;
                const selectedBillingInvoiceId = mergedInvoices[currentSelectedInvoiceId]
                    ? currentSelectedInvoiceId
                    : incomingDocuments[0]?.id || Object.keys(mergedInvoices)[0] || null;

                state.passiveWorkspace = recalculatePassiveWorkspace({
                    ...(state.passiveWorkspace || {}),
                    invoices: mergedInvoices,
                    selectedInvoiceId: selectedBillingInvoiceId,
                });
                state.treasuryWorkspace = recalculateTreasuryWorkspace({
                    treasuryWorkspace: state.treasuryWorkspace,
                    passiveWorkspace: state.passiveWorkspace,
                    documents: state.documents,
                });
                state.billingHubPassive = {
                    loading: false,
                    loaded: true,
                    error: null,
                    lastSyncedAt: dayjs().toISOString(),
                    companyDocumentId: action.meta?.arg?.companyDocumentId || incomingDocuments[0]?.companyDocumentId || state.activeCompanyDocumentId,
                };
                state.lastSyncedAt = dayjs().toISOString();
                persistState(state);
            })
            .addCase(syncPassiveInvoicesFromBillingHub.rejected, (state, action) => {
                state.billingHubPassive = {
                    ...(state.billingHubPassive || {}),
                    loading: false,
                    loaded: true,
                    error: action.payload || 'Errore sync Billing Hub',
                    companyDocumentId: action.meta?.arg?.companyDocumentId || state.billingHubPassive?.companyDocumentId || null,
                };
            })
            .addCase(hydrateRoadmapWorkspaceFallback.pending, (state) => {
                state.loadingWorkspace = true;
                state.errorWorkspace = false;
            })
            .addCase(hydrateRoadmapWorkspaceFallback.fulfilled, (state, action) => {
                state.loadingWorkspace = false;
                state.loadedWorkspace = true;
                state.errorWorkspace = false;
                state.activeCompanyDocumentId = action.payload.activeCompanyDocumentId;
                state.businessRole = action.payload.businessRole;
                state.companies = action.payload.companies;
                state.documents = action.payload.documents;
                state.archiveDocuments = action.payload.archiveDocuments || [];
                state.selectedFiscalYears = action.payload.selectedFiscalYears || [dayjs().year()];
                state.closedFiscalYears = action.payload.closedFiscalYears || Array.from({ length: 10 }, (_, i) => dayjs().year() - 10 + i);
                state.passiveWorkspace = action.payload.passiveWorkspace;
                state.treasuryWorkspace = action.payload.treasuryWorkspace;
                state.inventoryWorkspace = action.payload.inventoryWorkspace;
                state.foreignInvoiceWorkspace = action.payload.foreignInvoiceWorkspace;
                state.activeWorkspace = action.payload.activeWorkspace;
                if (action.payload.complianceWorkspace) {
                    state.complianceWorkspace = action.payload.complianceWorkspace;
                }
                if (action.payload.billingWorkspace) {
                    state.billingWorkspace = action.payload.billingWorkspace;
                }
                if (action.payload.migrationWorkspace) {
                    state.migrationWorkspace = action.payload.migrationWorkspace;
                }
                if (action.payload.templateEditorWorkspace) {
                    state.templateEditorWorkspace = action.payload.templateEditorWorkspace;
                }
                if (action.payload.accountantPackageWorkspace) {
                    state.accountantPackageWorkspace = action.payload.accountantPackageWorkspace;
                }
                state.notifications = action.payload.notifications;
                state.playbooks = action.payload.playbooks;
                state.helpArticles = action.payload.helpArticles;
                state.pendingSaves = action.payload.pendingSaves;
                state.networkMode = action.payload.networkMode;
                state.lastSyncedAt = action.payload.lastSyncedAt;
                state.loadedAt = action.payload.loadedAt;
                persistState(state);
            })
            .addCase(hydrateRoadmapWorkspaceFallback.rejected, (state) => {
                state.loadingWorkspace = false;
                state.loadedWorkspace = true;
                state.errorWorkspace = true;
            });
    },
});

export const {
    setNetworkMode,
    markNotificationRead,
    markAllNotificationsRead,
    dismissNotification,
    setActiveRoadmapCompany,
    setSelectedFiscalYears,
    toggleFiscalYearClosed,
    setSelectedTreasurySource,
    setSelectedTreasuryPayable,
    setSelectedTreasuryLedgerSupplier,
    setSelectedInventoryDeposit,
    setSelectedInventorySerial,
    repairRoadmapWorkspaceFromSeed,
    setSelectedPassiveInvoice,
    replacePassiveDdts,
    setPassiveSelectedDdts,
    applyPassiveAutoMatch,
    updatePassiveLine,
    updatePassiveJournalGroup,
    addPassiveExtraLine,
    removePassiveExtraLine,
    togglePassiveServiceFlow,
    addPassiveDirectLine,
    removePassiveDirectLine,
    promotePassiveSupplierFromXml,
    linkRealSupplierToInvoice,
    unlinkSupplierFromInvoice,
    addImportedStatementLocally,
    registerTreasuryPaymentLocally,
    applyTreasuryCreditCompensationLocally,
    importInventoryCatalogLocally,
    createInventoryTransferLocally,
    startInventoryTrialLocally,
    consumeInventoryMaterialLocally,
    generateInventoryReorderLocally,
    startMockSave,
    queueMockSave,
    markMockSaveSaving,
    completeMockSave,
    commitPassiveInvoiceLocally,
    applyDocumentTransition,
    setActiveForeignInvoice,
    addForeignInvoiceLocally,
    updateForeignInvoiceLocally,
    addCustomsDeclarationLocally,
    updateCustomsDeclarationLocally,
    addCustomsCostLocally,
    removeCustomsCostLocally,
    updateCustomsCostLocally,
    commitForeignInvoiceLocally,
    createReturnDdtLocally,
    updateReturnDdtLineLocally,
    updateReturnDdtNoteLocally,
    submitReturnDdtLocally,
    linkCreditNoteToReturnLocally,
    setActiveAgreementId,
    setActiveQueueItemId,
    setActiveCreditNoteId,
    addActiveAgreementLocally,
    updateActiveAgreementLocally,
    confirmActiveAgreementLocally,
    sendActiveAgreementToAdminLocally,
    pushPatientAgreementToQueueLocally,
    markQueueItemCompleteLocally,
    updateQueueItemStatusLocally,
    addActiveCreditNoteFromInvoiceLocally,
    addActiveCreditNoteLocally,
    updateActiveCreditNoteLocally,
    emitActiveCreditNoteLocally,
    addAssistedReInvoiceFromCreditNoteLocally,
    addActiveReInvoiceLocally,
    updateActiveReInvoiceLocally,
    emitActiveReInvoiceLocally,
    addActiveReceiptLocally,
    updateActiveReceiptLocally,
    emitActiveReceiptLocally,
    cancelActiveReceiptLocally,
    convertTrialToAgreementLocally,
    setActiveNsoOrderId,
    addNsoOrderLocally,
    setActivePraticaId,
    setActiveFatturaPAId,
    setActiveStsExportId,
    receiveNsoOrderLocally,
    evadeNsoOrderLocally,
    addPraticaAslLocally,
    updatePraticaAslLocally,
    advancePraticaAslToProva,
    advancePraticaAslToConsegnato,
    advancePraticaAslToFatturato,
    advancePraticaAslToPagato,
    addFatturaPALocally,
    emitFatturaPALocally,
    addStsExportLocally,
    prepareStsExportLocally,
    submitStsExportLocally,
    downloadStsLightLocally,
    addAccountantExportLocally,
    markAccountantExportSentLocally,
    setBillingChannel,
    setActiveB2bInvoice,
    addRegistroSezionaleLocally,
    updateRegistroSezionaleLocally,
    deleteRegistroSezionaleLocally,
    toggleRegistroSezionaleActive,
    addB2bInvoiceLocally,
    updateB2bInvoiceLocally,
    emitB2bInvoiceLocally,
    setActiveFreeInvoiceId,
    addFreeInvoiceLocally,
    updateFreeInvoiceLocally,
    emitFreeInvoiceLocally,
    reconcileFreeInvoiceLocally,
    setActiveDdtId,
    addActiveDdtLocally,
    updateActiveDdtLocally,
    emitActiveDdtLocally,
    createInvoiceFromDdtsLocally,
    emitImmediateInvoiceLocally,
    setActiveMigrationId,
    initPraticaMigrationLocally,
    completePraticaMigrationLocally,
    cancelPraticaMigrationLocally,
    setActiveTemplateId,
    addTemplateLocally,
    updateTemplateLocally,
    updateTemplateFieldMappingLocally,
    activateTemplateLocally,
    archiveTemplateLocally,
    setActivePackageId,
    generateAccountantPackageLocally,
    markAccountantPackageSentLocally,
    deleteAccountantPackageLocally,
} = financeRoadmapSlice.actions;

export const selectRoadmapCompany = (state) => {
    const activeCompanyDocumentId = state.financeRoadmap.activeCompanyDocumentId || resolveCompanyDocumentId(state.auth.azienda_in_uso);
    return state.financeRoadmap.companies.find((company) => company.documentId === activeCompanyDocumentId) || null;
};

export const selectClosedFiscalYears = (state) => state.financeRoadmap.closedFiscalYears || [];

export const selectRoadmapVisibleDocuments = (state) => {
    const roleKey = state.financeRoadmap.businessRole || resolveRoadmapBusinessRole(state.auth?.ruolo?.name);
    return state.financeRoadmap.documents.filter((document) => {
        if (roleKey === 'admin') {
            return true;
        }

        if (roleKey === 'contabile') {
            return document.domain !== 'commerciale';
        }

        return document.domain !== 'finance';
    });
};

export const selectFinanceQuickSearchIndex = (state) => {
    return buildFinanceQuickSearchEntries({
        documents: state.financeRoadmap.documents,
        playbooks: state.financeRoadmap.playbooks,
        companies: state.financeRoadmap.companies,
        treasuryWorkspace: state.financeRoadmap.treasuryWorkspace,
        inventoryWorkspace: state.financeRoadmap.inventoryWorkspace,
        roleKey: state.financeRoadmap.businessRole || resolveRoadmapBusinessRole(state.auth?.ruolo?.name),
    });
};

export const selectPassiveWorkspace = (state) => state.financeRoadmap.passiveWorkspace;

export const selectReturnDdts = (state) => state.financeRoadmap.passiveWorkspace?.returnDdts || [];

export const selectActivePassiveInvoice = (state) => {
    const workspace = state.financeRoadmap.passiveWorkspace;
    const selectedInvoiceId = workspace?.selectedInvoiceId;

    if (!selectedInvoiceId || !workspace?.invoices) {
        return null;
    }

    return workspace.invoices[selectedInvoiceId] || null;
};

export const selectTreasuryWorkspace = (state) => state.financeRoadmap.treasuryWorkspace;

export const selectActiveTreasurySource = (state) => {
    const workspace = state.financeRoadmap.treasuryWorkspace;
    return (workspace?.sources || []).find((source) => source.id === workspace?.activeSourceId) || null;
};

export const selectActiveTreasuryPayable = (state) => {
    const workspace = state.financeRoadmap.treasuryWorkspace;
    return (workspace?.payables || []).find((payable) => payable.id === workspace?.activePayableId) || null;
};

export const selectInventoryWorkspace = (state) => state.financeRoadmap.inventoryWorkspace;

export const selectActiveInventoryDeposit = (state) => {
    const workspace = state.financeRoadmap.inventoryWorkspace;
    return (workspace?.deposits || []).find((deposit) => deposit.id === workspace?.activeDepositId) || null;
};

export const selectActiveInventorySerial = (state) => {
    const workspace = state.financeRoadmap.inventoryWorkspace;
    return (workspace?.serials || []).find((serial) => serial.id === workspace?.selectedSerialId) || null;
};

export const selectForeignInvoiceWorkspace = (state) => state.financeRoadmap.foreignInvoiceWorkspace;

export const selectActiveForeignInvoice = (state) => {
    const workspace = state.financeRoadmap.foreignInvoiceWorkspace;
    const activeId = workspace?.activeForeignInvoiceId;

    if (!activeId) {
        return null;
    }

    return (workspace?.foreignInvoices || []).find((inv) => inv.id === activeId) || null;
};

export const selectActiveWorkspace = (state) => state.financeRoadmap.activeWorkspace;

export const selectActiveAgreement = (state) => {
    const workspace = state.financeRoadmap.activeWorkspace;
    const activeId = workspace?.activeAgreementId;
    if (!activeId) return null;
    return (workspace?.agreements || []).find((a) => a.id === activeId) || null;
};

export const selectActiveQueueItem = (state) => {
    const workspace = state.financeRoadmap.activeWorkspace;
    const activeId = workspace?.activeQueueItemId;
    if (!activeId) return null;
    return (workspace?.operativeQueue || []).find((q) => q.id === activeId) || null;
};

export const selectActiveCreditNote = (state) => {
    const workspace = state.financeRoadmap.activeWorkspace;
    const activeId = workspace?.activeCreditNoteId;
    if (!activeId) return null;
    return (workspace?.creditNotes || []).find((cn) => cn.id === activeId) || null;
};

export const selectComplianceWorkspace = (state) => state.financeRoadmap.complianceWorkspace;

export const selectActiveNsoOrder = (state) => {
    const workspace = state.financeRoadmap.complianceWorkspace;
    const activeId = workspace?.activeNsoOrderId;
    if (!activeId) return (workspace?.nsoOrders || [])[0] || null;
    return (workspace?.nsoOrders || []).find((o) => o.id === activeId) || null;
};

export const selectActivePratica = (state) => {
    const workspace = state.financeRoadmap.complianceWorkspace;
    const activeId = workspace?.activePraticaId;
    if (!activeId) return (workspace?.pratiche || [])[0] || null;
    return (workspace?.pratiche || []).find((p) => p.id === activeId) || null;
};

export const selectActiveFatturaPA = (state) => {
    const workspace = state.financeRoadmap.complianceWorkspace;
    const activeId = workspace?.activeFatturaPAId;
    if (!activeId) return (workspace?.fatturePA || [])[0] || null;
    return (workspace?.fatturePA || []).find((f) => f.id === activeId) || null;
};

export const selectActiveStsExport = (state) => {
    const workspace = state.financeRoadmap.complianceWorkspace;
    const activeId = workspace?.activeStsExportId;
    if (!activeId) return (workspace?.stsExports || [])[0] || null;
    return (workspace?.stsExports || []).find((s) => s.id === activeId) || null;
};

export const selectBillingWorkspace = (state) => state.financeRoadmap.billingWorkspace;

export const selectRegistriSezionali = (state) => state.financeRoadmap.billingWorkspace?.registriSezionali || [];

export const selectActiveB2bInvoice = (state) => {
    const workspace = state.financeRoadmap.billingWorkspace;
    const activeId = workspace?.activeB2bInvoiceId;
    if (!activeId) return (workspace?.b2bInvoices || [])[0] || null;
    return (workspace?.b2bInvoices || []).find((inv) => inv.id === activeId) || null;
};

export const selectFreeInvoices = (state) => state.financeRoadmap.billingWorkspace?.freeInvoices || [];

export const selectActiveFreeInvoice = (state) => {
    const workspace = state.financeRoadmap.billingWorkspace;
    const activeId = workspace?.activeFreeInvoiceId;
    if (!activeId) return null;
    return (workspace?.freeInvoices || []).find((inv) => inv.id === activeId) || null;
};

export const selectActiveDdts = (state) => state.financeRoadmap.billingWorkspace?.activeDdts || [];

export const selectPendingActiveDdts = (state) =>
    (state.financeRoadmap.billingWorkspace?.activeDdts || []).filter((d) => d.status === 'emesso');

export const selectActiveDdt = (state) => {
    const workspace = state.financeRoadmap.billingWorkspace;
    const activeId = workspace?.activeDdtId;
    if (!activeId) return null;
    return (workspace?.activeDdts || []).find((d) => d.id === activeId) || null;
};

export const selectMigrationWorkspace = (state) => state.financeRoadmap.migrationWorkspace;

export const selectActiveMigration = (state) => {
    const workspace = state.financeRoadmap.migrationWorkspace;
    const activeId = workspace?.activeMigrationId;
    if (!activeId) return null;
    return (workspace?.migrations || []).find((m) => m.id === activeId) || null;
};

export const selectTemplateEditorWorkspace = (state) => state.financeRoadmap.templateEditorWorkspace;

export const selectActiveTemplate = (state) => {
    const workspace = state.financeRoadmap.templateEditorWorkspace;
    const activeId = workspace?.activeTemplateId;
    if (!activeId) return (workspace?.templates || [])[0] || null;
    return (workspace?.templates || []).find((t) => t.id === activeId) || null;
};

export const selectAccountantPackageWorkspace = (state) => state.financeRoadmap.accountantPackageWorkspace;

export const selectAccountantPackages = (state) =>
    state.financeRoadmap.accountantPackageWorkspace?.packages || [];

export const selectActiveAccountantPackage = (state) => {
    const workspace = state.financeRoadmap.accountantPackageWorkspace;
    const activeId = workspace?.activePackageId;
    if (!activeId) return (workspace?.packages || [])[0] || null;
    return (workspace?.packages || []).find((p) => p.id === activeId) || null;
};

export default financeRoadmapSlice.reducer;
